import { LanguageOption, Language, Translations } from "./types.js";

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "ln", name: "Lingala", nativeName: "Lingála", flag: "🇨🇩" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili", flag: "🇹🇿" },
];

export const translations: Record<Language, Translations> = {
  en: {
    common: {
      appName: "HP - Helix",
      tagline: "Next-Gen Resilient Video Streaming & Direct Monetization",
      upload: "Upload Studio",
      player: "Video Player",
      architecture: "Pipeline Architecture",
      ready: "Ready",
      processing: "Processing",
      uploading: "Uploading",
      failed: "Failed",
      paused: "Paused",
      cancel: "Cancel",
      pause: "Pause",
      resume: "Resume",
      save: "Save",
      loading: "Loading...",
      error: "Error",
      success: "Success",
      auto: "Auto",
    },
    nav: {
      home: "Home",
      shorts: "Shorts",
      trending: "Trending",
      subscriptions: "Subscriptions",
      history: "History",
      offlineLibrary: "Offline Library",
      uploadStudio: "Upload Studio",
      studio: "HP-Helix Studio",
      adminPortal: "Admin Portal",
      architecture: "Pipeline Architecture",
      settings: "Settings",
      all: "All",
      music: "Music",
      podcasts: "Podcasts",
      tech: "Tech",
      entertainment: "Entertainment",
      cinema: "Cinema",
      africanCinema: "Cinema",
      gaming: "Gaming",
      culture: "Culture & News",
      viewChannel: "View your channel",
      uploadVideo: "Upload video",
      signOut: "Sign Out",
    },
    home: {
      recommendedVideos: "Recommended Videos",
      videosAvailable: "{{count}} videos available",
      views: "{{count}} views",
      view: "{{count}} view",
      hoursAgo: "{{count}} hours ago",
      daysAgo: "{{count}} days ago",
      weeksAgo: "{{count}} weeks ago",
      justNow: "Just now",
      recently: "Recently",
    },
    studio: {
      uploadVideos: "Upload videos",
      dragDropTitle: "Drag and drop video files to upload",
      dragDropSubtitle: "Your videos will be private until you publish them. Supports MP4, WebM, MOV, and high-bitrate ProRes.",
      videoFormat: "Video Format & Experience",
      standardVideo: "Standard Video (16:9)",
      shortReel: "Short / Reel (9:16)",
      relatedVideo: "Related video",
      relatedVideoDesc: "Link this Short directly to its source long-form video. Viewers will see a clickable pill overlay to jump directly into the full video.",
      selectLongVideo: "Select Long-Form Video",
      noneStandalone: "-- None / Standalone Short --",
      jumpTimestamp: "Jump to timestamp in long video (seconds or mm:ss)",
      audience: "Audience",
      audienceQuestion: "Is this video made for kids? (required)",
      coppaNotice: "Regardless of your location, you're legally required to comply with the Children's Online Privacy Protection Act (COPPA).",
      madeForKidsYes: "Yes, it's made for kids",
      madeForKidsNo: "No, it's not made for kids",
      next: "Next",
      back: "Back",
      publishVideo: "Publish Video",
      publishing: "Publishing...",
      uploadCompleteChecksPassed: "Upload complete. Checks passed.",
      channelDashboard: "Channel dashboard",
      welcomeCreator: "Welcome to your creator headquarters, @{{handle}}",
      latestVideoPerformance: "Latest video performance",
      channelAnalytics: "Channel analytics",
      currentSubscribers: "Current subscribers",
      summaryLast28Days: "Summary (Last 28 days)",
      views: "Views",
      watchTimeHours: "Watch time (hours)",
      estimatedFanSupport: "Estimated Fan Support",
      creatorInsightsNews: "Creator insights & news",
      uploadVideo: "Upload Video",
      askStudio: "Ask Studio",
      switchToApp: "Switch to App",
      edgeTitle: "High-Speed Pan-African Streaming Active",
      edgeDescription: "HP-Helix Edge Delivery is serving optimized, low-data streams across Kinshasa, Goma, Lubumbashi, Nairobi, and regional hubs.",
      audienceRetention: "Audience retention",
      averageViewDuration: "Average view duration",
      averagePercentageViewed: "Average percentage viewed",
      retentionSubtitle: "Retention curve across the video's duration",
      selectVideo: "Select video",
      introBenchmark: "Intro benchmark (00:30)",
      introDropoff: "Intro retention (00:30)",
      introGreat: "Great intro hook rate",
      introTypical: "Typical drop-off",
      introNote: "{pct}% of viewers are still watching at 00:30. Strong early hooks reduce bounce rates.",
      spikesTitle: "Spikes and rewatches",
      spikesNote: "Spikes indicate portions of the video that viewers watched again or shared directly with friends.",
      rewatch: "Rewatch",
      ofTotal: "of {total} total",
      retentionNoData: "Not enough playback data yet",
      retentionNoDataHint: "Retention will appear here once viewers have watched this video.",
      retentionLoading: "Loading retention data…",
      retentionError: "Couldn't load retention data.",
      retentionForbidden: "You don't have access to this video's analytics.",
      sidebar: {
        dashboard: "Dashboard",
        content: "Content",
        playlists: "Playlists",
        analytics: "Analytics",
        community: "Community",
        subtitles: "Subtitles",
        contentDetection: "Content detection",
        earn: "Earn",
        customization: "Customization",
        audioLibrary: "Audio library",
        settings: "Settings",
        sendFeedback: "Send feedback",
      },
      goLive: "Go live",
    },
    watch: {
      createdFrom: "Created from:",
      subscribe: "Subscribe",
      subscribed: "Subscribed",
      like: "Like",
      liveChat: "Live Chat",
      superChat: "Super Chat",
      watchingNow: "watching now",
      share: "Share",
      download: "Download",
      comments: "Comments",
      writeComment: "Write a comment...",
      comment: "Comment",
      cancel: "Cancel",
      tipCreator: "Tip Creator",
      reply: "Reply",
      viewReplies: "View replies",
      hideReplies: "Hide replies",
      pinnedBy: "Pinned by",
      pinComment: "Pin comment",
      unpinComment: "Unpin comment",
    },
    tipping: {
      tipCreator: "Tip Creator",
      merci: "Merci",
      modalTitle: "Tip Creator",
      modalSubtitle: "Direct Mobile Money support via Airtel Money, Vodacom M-Pesa, or Orange Money",
      currency: "Currency",
      presetAmounts: "Preset Amounts",
      customAmount: "Custom Amount",
      selectProvider: "Select Mobile Money Provider",
      phoneNumber: "Mobile Money Phone Number",
      phoneNumberPlaceholder: "81 234 5678",
      message: "Supporter Message",
      messagePlaceholder: "Add a message of appreciation (optional)...",
      sendTip: "Send Tip",
      sending: "Processing Mobile Money...",
      tipSuccess: "Thank You for Your Support!",
      tipSuccessDesc: "Your tip has been transferred directly to the creator.",
      tippedBadge: "Tipped",
      superThanks: "Super Thanks",
      feeBreakdown: "80% goes directly to creator, 20% platform infrastructure fee",
    },
    admin: {
      dashboardTitle: "Global Admin Dashboard",
      operationsTitle: "Platform Operations & Moderation",
      totalVolume: "Total Volume",
      platformMargin: "Platform Margin (20%)",
      creatorNet: "Creator Net (80%)",
      catalogHealth: "Catalog Health",
      moderationTab: "AI Moderation Queue",
      payoutsTab: "Creator Payout Queue",
      tableType: "Type",
      tableContent: "Content / Title",
      tableAuthor: "Author",
      tableReason: "Flag Reason",
      tableScore: "Toxicity Score",
      tableStatus: "Status",
      tableActions: "Actions",
      tableRequestId: "Request ID",
      tableCreator: "Creator / Channel",
      tableAmount: "Amount",
      tableMethod: "Payout Method",
      tablePhone: "Phone Number",
      approve: "Approve",
      reject: "Remove / Reject",
      processPayout: "Process Payout",
      pageNotAvailable: "This page isn't available",
      pageNotFoundDesc: "The link you followed may be broken, or the page may have been removed or does not exist.",
      commandCenter: "Command Center",
      switchboard: "Switchboard",
      systemLogs: "System Logs",
      concurrentViewers: "Concurrent Viewers",
      activeBroadcasts: "Active Broadcasts",
      bandwidthEgress: "24h Bandwidth Egress",
      edgeCacheHitRate: "Edge Cache Hit Rate",
      momoGmv30d: "MoMo GMV (30D)",
      pendingModeration: "Pending Moderation",
      pendingDisputes: "Pending Disputes",
      pendingPayouts: "Pending Payouts",
      regionalConcentration: "Regional Viewer Concentration",
      emergencyMaintenance: "Emergency Maintenance Mode",
      emergencyMaintenanceDesc: "Immediately locks the public platform behind a holding gate.",
      registrationLock: "Creator Registration Lock",
      registrationLockDesc: "Temporarily halts new creator and channel onboarding.",
      payoutEngine: "Mobile Money Payout Engine",
      payoutEngineDesc: "Enables automated disbursement batch settlements to creator wallets.",
      highBitrateCap: "High-Bitrate Cap (Data Saver)",
      highBitrateCapDesc: "Enforces a maximum 720p cap during regional fiber bottlenecks.",
      confirmToggleTitle: "Confirm Switchboard Change",
      confirmToggleDesc: "This action immediately applies across all Edge PoPs and generates an immutable audit record.",
      confirmAction: "Confirm & Apply",
      cancelAction: "Cancel",
      auditTrail: "Immutable Audit Trail",
      actionFilter: "Filter by action",
      entityFilter: "Filter by entity",
      appliedBy: "Applied by",
      timestamp: "Timestamp",
      statusHealthy: "Operational / Healthy",
    },
    upload: {
      pageTitle: "Creator Upload Studio",
      pageSubtitle: "Direct-to-Cloudflare Stream Resumable Uploads via TUS Protocol",
      directUploadNotice: "Zero Backend Buffering: Video files stream directly from your browser to Cloudflare global ingest nodes.",
      dragDropText: "Drag and drop your video file here",
      orBrowse: "or click to select file from device",
      supportedFormats: "Supports MP4, MOV, MKV, WebM up to 30GB",
      selectedFile: "Selected File",
      fileSize: "File Size",
      titleLabel: "Video Title",
      titlePlaceholder: "Enter an engaging video title...",
      descLabel: "Description",
      descPlaceholder: "Provide details, credits, or links for your audience...",
      categoryLabel: "Category",
      languageLabel: "Audio / Primary Language",
      creatorProfileIdLabel: "Creator Profile ID",
      creatorProfileIdPlaceholder: "e.g., clxyz123abc or your creator profile ID",
      maxDurationLabel: "Max Duration (Seconds)",
      startUploadButton: "Start Direct Upload to Cloudflare Stream",
      uploadProgress: "Upload Progress",
      uploadSpeed: "Speed",
      eta: "Est. Time Remaining",
      uploadedBytes: "Uploaded",
      tusResumableTag: "TUS Protocol: Resumable chunk upload active",
      sessionCreated: "Authenticated session generated on Cloudflare Stream",
      uploadSuccessTitle: "Upload Completed Successfully!",
      uploadSuccessDesc: "Your video has landed on Cloudflare Stream and multi-bitrate HLS encoding is initializing.",
      streamIdLabel: "Stream Media ID (UID)",
      hlsUrlLabel: "Adaptive HLS Manifest URL",
      watchPreviewButton: "Preview in Adaptive Player",
      uploadAnotherButton: "Upload Another Video",
      errors: {
        noFile: "Please select a video file to upload.",
        noTitle: "Video title is required.",
        noCreatorId: "Creator Profile ID is required.",
        sessionFailed: "Failed to obtain Cloudflare Stream direct-upload URL from backend.",
        uploadFailed: "TUS direct upload encountered an error.",
      },
    },
    categories: {
      music: "Music & Performance",
      education: "Education & Tutorials",
      gaming: "Gaming & Esports",
      entertainment: "Entertainment & Comedy",
      cultureNews: "Culture & Documentaries",
      technology: "Technology & Coding",
      lifestyle: "Lifestyle & Vlogs",
    },
    player: {
      pageTitle: "Adaptive HLS Player",
      pageSubtitle: "Multi-bitrate low-latency video playback with quality switching & offline downloads",
      enterHlsOrUid: "Search videos, artists, podcasts...",
      loadVideo: "Load Stream",
      quality: "Quality",
      speed: "Playback speed",
      normal: "Normal",
      sourceLocked: "Source locked",
      downloadOffline: "Download MP4 for Offline Viewing",
      downloadMp4Notice: "Original Cloudflare Stream MP4 direct download",
      downloading: "Preparing download...",
      currentResolution: "Current Resolution",
      bitrate: "Bitrate",
      liveStats: "Stream Telemetry",
      showStats: "Show Stream Stats",
      hideStats: "Hide Stream Stats",
      pip: "Picture in Picture",
      fullscreen: "Fullscreen",
      mute: "Mute",
      unmute: "Unmute",
      play: "Play",
      pause: "Pause",
      buffering: "Buffering...",
      streamMediaId: "Stream Media ID",
      demoVideoTitle: "Helix Adaptive HLS Showcase Video",
      demoVideoCreator: "HP - Helix Streaming Engine",
      chapters: "Chapters",
      chapter: "Chapter",
      autoChapters: "Enable auto chapters from description",
      endScreen: "End screen",
      subscribe: "Subscribe",
      subscribed: "Subscribed",
      watchNext: "Watch next",
      audioTrack: "Audio track",
      originalAudio: "Original audio",
      dubbedAudio: "Dubbed audio",
      audioTrackDesc: "Multi-language audio tracks",
      addAudioDub: "Add Audio Dub",
    },
    arch: {
      title: "Cloudflare Stream + Render Invariant Architecture",
      subtitle: "Enterprise-grade high-throughput streaming architecture with 0 backend disk/RAM saturation.",
      zeroBackendRuleTitle: "Core Invariant: Zero Raw Video Buffering",
      zeroBackendRuleDesc: "Client video binaries never touch or transit the Render Fastify backend. All uploads go directly from client to Cloudflare Stream via authenticated TUS direct-upload URLs.",
      pipelineSteps: {
        step1Title: "1. Authenticated Direct Session",
        step1Desc: "Client requests direct upload session from Fastify API. Backend requests one-time TUS token from Cloudflare Stream and creates DB record.",
        step2Title: "2. Client TUS Direct Chunk Streaming",
        step2Desc: "Client uploads chunks straight to Cloudflare Stream edge nodes with automatic retry & pause/resume capabilities.",
        step3Title: "3. Multi-Bitrate HLS Transcoding",
        step3Desc: "Cloudflare Stream transcodes the source into 1080p, 720p, 480p, 360p, 240p, 144p adaptive bitrate manifests.",
        step4Title: "4. Webhook Reconciliation & Low-Latency Delivery",
        step4Desc: "Cloudflare notifies Render backend via webhook; manifest becomes available for instant global CDN streaming and MP4 offline downloads.",
      },
    },
    notifications: {
      title: "Notifications",
      all: "All",
      personalized: "Personalized",
      none: "None",
      markAllAsRead: "Mark all as read",
      empty: "No new notifications",
      newUpload: "New upload",
      tierUpdated: "Notification preference updated",
    },
    community: {
      community: "Community",
      postsAndPolls: "Posts & Polls",
      createPost: "Create a post",
      postPlaceholder: "Share an update with your community or ask your fans a question...",
      addPoll: "Add poll",
      removePoll: "Remove poll",
      pollOptions: "Poll Options (2 to 5)",
      option: "Option",
      addOption: "Add option",
      public: "Public",
      membersOnly: "Members Only",
      publish: "Publish",
      publishing: "Publishing...",
      postPublished: "Post published successfully!",
      contentRequired: "Please enter some content for your post.",
      pollMinOptions: "A poll requires at least 2 non-empty options.",
      publishedPosts: "Published Posts",
      noPosts: "No community posts yet",
      noPostsDesc: "When this creator posts updates or polls, they will appear here.",
      deletePost: "Delete post",
      likes: "likes",
      vote: "Vote",
      votesCount: "votes",
      selectToVote: "Select an option to vote",
      clickToChangeVote: "Click any option to switch vote",
      loadingPosts: "Loading community posts...",
    },
    verification: {
      title: "Channel Badges & Verification",
      badgeVerified: "Verified Creator",
      badgeOfficialArtist: "Official Artist Channel",
      badgePartner: "Premier Partner",
      eligibilityTitle: "Eligibility Criteria Meter",
      applyButton: "Apply for Verification",
      statusPending: "Application Under Review",
      statusApproved: "Verified",
      statusRejected: "Application Rejected",
    },
    pwa: {
      installApp: "Install HP-Helix App",
      installDesc: "Experience ultra low-latency playback, offline downloads, and native app performance directly on your device.",
      installButton: "Install App",
      dismiss: "Maybe later",
      pushTitle: "Device Push Notifications",
      pushDesc: "Receive real-time alerts when creators you follow upload new videos or go live.",
      enablePush: "Enable Notifications",
      disablePush: "Disable Notifications",
      sendTestPush: "Send Test Notification",
      testPushSuccess: "Test notification dispatched to your browser!",
      permissionBlocked: "Notifications blocked. Please grant permission in your browser site settings.",
      installedBanner: "HP-Helix is installed and ready for offline use.",
    },
    experiments: {
      testAndCompare: "Test & Compare",
      testAndCompareDesc: "Test up to 3 thumbnails and alternate titles with your audience. The algorithm optimizes for Watch Time or Click-Through Rate.",
      startTest: "Start A/B Test",
      stopTest: "Stop Test",
      pickWinner: "Select as Winner",
      winningVariant: "Winning variant",
      leading: "Leading",
      slotA: "Slot A (Original)",
      slotB: "Slot B (Variant 2)",
      slotC: "Slot C (Variant 3)",
      addSlotC: "+ Add 3rd Thumbnail (Optional)",
      removeSlotC: "Remove Slot C",
      metricLabel: "Optimization Metric",
      metricWatchTime: "Watch Time Share (Recommended for algorithmic reach)",
      metricCtr: "Click-Through Rate (CTR)",
      impressions: "Impressions",
      clicks: "Clicks",
      ctrRate: "CTR",
      watchTimeShare: "Watch Time Share",
      runningTest: "Test in Progress",
      completedTest: "Test Completed",
      stoppedTest: "Test Stopped",
      alternateTitle: "Alternate Title (Optional)",
      noActiveTest: "No active test running for this video.",
      winnerPromotedSuccess: "Variant successfully promoted as the permanent thumbnail and title!",
    },
    memberships: {
      join: "Join",
      memberships: "Memberships",
      membersOnly: "Members Only",
      membershipTiers: "Membership Tiers",
      joinChannel: "Join Channel",
      joinDescription: "Unlock exclusive perks, subscriber loyalty badges, and direct creator access.",
      monthlyPerks: "Monthly Perks",
      loyaltyBadge: "Loyalty Badge",
      chooseTier: "Choose Your Membership Tier",
      momoPayment: "Select Mobile Money Operator",
      activeMembers: "Active Members",
      monthlyRevenue: "Estimated MRR",
      createTier: "Add Membership Tier",
      tierName: "Tier Name",
      priceUsd: "Monthly Price ($ USD)",
      priceCdf: "Monthly Price (CDF)",
      perks: "Tier Perks",
      subscribedSince: "Member Since",
      monthsMember: "months member",
    },
    premieres: {
      premiere: "Premiere",
      livePremiere: "Live Premiere",
      premiereStartsIn: "Premiere starts in",
      remindMe: "Remind me",
      reminderSet: "Reminder set",
      premiereCountdown: "Premiere Countdown",
      setAsPremiere: "Schedule Premiere",
      waitingRoomNotice: "Waiting room active. Chat with other viewers before the premiere begins.",
      joinLiveChat: "Join Live Chat",
    },
    recommendations: {
      upNext: "Up next",
      autoplayOn: "Autoplay is on",
      autoplayOff: "Autoplay is off",
      cancel: "Cancel",
      playNow: "Play Now",
      regionalTrending: "Trending in DRC & East Africa",
      dataSaverActive: "Data Saver active",
      similarCategory: "Similar category",
      fromFavoriteChannel: "From channels you watch",
    },
    maintenance: {
      headline: "HP-Helix Video Streaming | Coming Soon",
      subtitle: "The video platform for DRC and regional Africa is currently in private preview testing.",
      badge: "Platform Maintenance",
      devAccess: "Developer Access / Creator Early Access",
      devModalTitle: "Developer & Creator Early Access",
      devModalSubtitle: "Enter the alpha passkey to unlock the complete platform experience.",
      passcodePlaceholder: "Enter access passkey (e.g. helix_alpha_2026)...",
      unlockButton: "Unlock Platform",
      unlocking: "Unlocking...",
      invalidPasscode: "Invalid passkey. Please check credentials and try again.",
      featureHlsTitle: "Ultra-Low Latency 4K HLS",
      featureHlsDesc: "Direct Cloudflare Stream edge CDN ingestion with zero backend video binary transit.",
      featureMobileTitle: "Mobile-First Data Saver",
      featureMobileDesc: "Optimized for DR Congo and East Africa network topologies with offline caching.",
      featureMonetizationTitle: "Direct Creator Payouts",
      featureMonetizationDesc: "Instant Mobile Money integration for M-Pesa, Airtel Money, and Orange Money.",
      featureChatTitle: "Live Premieres & Chat",
      featureChatDesc: "Synchronized viewer countdown clocks and real-time community chat streams.",
    },
    clips: {
      clip: "Clip",
      createClip: "Create Clip",
      watchFullVideo: "Watch Full Video",
      shareClip: "Share Clip",
      clipDuration: "Clip Duration",
      titlePlaceholder: "Add a title for your clip (e.g. Best chorus drop)...",
      titleRequired: "Title is required (max 100 characters)",
      characterCount: "characters",
      previewLoop: "Preview Loop",
      copiedLink: "Clip link copied to clipboard!",
      copyLink: "Copy Link",
      shareWhatsApp: "Share to WhatsApp",
      shareX: "Share on X",
      shareFacebook: "Share on Facebook",
      minDurationError: "Clip must be at least 5 seconds long.",
      maxDurationError: "Clip cannot exceed 60 seconds.",
      creating: "Creating Clip...",
      viewClip: "View Clip",
      viewsCount: "views",
      startTime: "Start time",
      endTime: "End time",
    },
    studioUpload: {
      uploadVideo: "Upload video",
      selectFromVideoFrames: "Select from video frames",
      uploading: "Uploading...",
      standardVideoBadge: "Standard Video (16:9)",
      shortBadge: "Helix Short (9:16)",
      customThumbnail: "Custom Thumbnail",
      uploadCustomThumbnail: "Upload Custom Thumbnail",
      uploadSpeed: "Upload speed",
      timeRemaining: "Time remaining",
      pauseUpload: "Pause",
      resumeUpload: "Resume",
      cancelUpload: "Cancel",
      visibilityLabel: "Visibility",
      monetizationOptIn: "Enable Paywall & Mobile Money Monetization",
      mobileMoneyPayouts: "Airtel Money / M-Pesa / Orange Money Payouts",
      titleAutoPopulated: "Auto-detected title from file",
      probingMedia: "Analyzing format & extracting thumbnails...",
    },
    merch: {
      store: "Store",
      merchandise: "Official Merchandise",
      officialMerch: "Official Merchandise",
      buyNow: "Buy Now",
      order: "Order",
      inStock: "In Stock",
      outOfStock: "Out of Stock",
      deliveryCity: "Delivery City",
      deliveryAddress: "Street Address",
      buyerName: "Your Full Name",
      buyerPhone: "Mobile Money Number (M-Pesa / Airtel / Orange)",
      paymentMethod: "Payment Method",
      momoPaymentPrompt: "A Mobile Money payment authorization prompt will be sent to your phone.",
      placeOrder: "Confirm Order with Mobile Money",
      placingOrder: "Sending MoMo Prompt...",
      orderSuccess: "Order Placed Successfully!",
      orderSuccessDesc: "Please check your phone and enter your Mobile Money PIN to authorize payment.",
      pinnedMerch: "Featured Creator Products",
      allProducts: "All Products",
      apparel: "Apparel",
      musicVinyl: "Music & Vinyl",
      accessories: "Accessories",
      close: "Close",
    },
    moderation: {
      heldForReview: "Held for review",
      published: "Published",
      reportContent: "Report content",
      reportSuccess: "Thank you for reporting. This content is under review.",
      approve: "Approve",
      remove: "Remove",
      blockAuthor: "Block author",
      approveAll: "Approve all",
      removeAll: "Remove all",
      commentsQueue: "Comments Review Queue",
      noHeldComments: "No comments held for review.",
      noPublishedComments: "No published comments yet.",
      selectAll: "Select all",
      selectedCount: "selected",
      reportReason: "Select violation reason",
      reasonSpam: "Misleading or Spam",
      reasonHateSpeech: "Hate Speech or Harassment",
      reasonCopyright: "Copyright Infringement",
      reasonExplicit: "Explicit or Violent Content",
      additionalDetails: "Additional details (optional)",
      detailsPlaceholder: "Provide extra context to help our moderation team...",
      submitReport: "Submit Report",
      cancel: "Cancel",
      authorBlockedNotice: "Author has been blocked from commenting.",
      commentApprovedNotice: "Comment approved and published.",
      commentRemovedNotice: "Comment removed.",
    },
    live: {
      live: "LIVE",
      liveBadge: "LIVE",
      rewindToLive: "Go to Live",
      streamEnded: "Stream has ended",
      enableDvr: "Enable DVR Window (Rewind buffer up to 2 hours)",
      enableDvrDesc: "Allows viewers to pause and seek backwards while streaming live.",
      autoPublishVod: "Auto-publish as VOD Replay",
      autoPublishVodDesc: "Automatically converts the stream into a permanent video replay once ended.",
      liveReplay: "Live Replay",
      goToLive: "Go to Live",
      liveStream: "Live Stream",
    },
    playlists: {
      playlists: "Playlists",
      playlist: "Playlist",
      saveToPlaylist: "Save",
      createPlaylist: "Create new playlist",
      playlistTitle: "Playlist Title",
      privacy: "Privacy",
      public: "Public",
      unlisted: "Unlisted",
      private: "Private",
      shuffle: "Shuffle",
      loop: "Loop playlist",
      loopOn: "Loop playlist: ON",
      loopOff: "Loop playlist: OFF",
      mix: "Mix",
      mixDesc: "Continuous smart mix based on this video",
      videoCount: "videos",
      emptyPlaylist: "No playlists found. Create one below!",
    },
    panoramic: {
      panoramicVideo: "360° Video",
      vrMode: "VR Mode",
      cardboardMode: "Cardboard VR Mode",
      dragToLook: "Drag to look around",
      tiltDevice: "Tilt device to look around",
      resetBearing: "Reset to North",
      compass: "Compass",
      projectionEquirectangular: "Equirectangular (360° Sphere)",
      projectionFlat: "Flat Projection",
      recordedIn360: "Recorded in 360° / VR format",
      recordedIn360Desc: "Enable 360° panoramic canvas and spatial navigation for this video.",
    },
    offlineVault: {
      download: "Download",
      downloading: "Downloading...",
      downloaded: "Downloaded",
      offlineLibrary: "Offline Library",
      storage: "Storage",
      storageUsed: "Storage Used",
      clearAll: "Clear All",
      deleteFromDevice: "Delete from device",
      dataSaver: "Data Saver (360p)",
      standardQuality: "Standard (480p)",
      highDefinition: "High Definition (720p)",
      offlineModeActive: "Offline Mode Active",
      offlineModeNotice: "Offline mode active - Playing from your local library",
      noVideosDownloaded: "No offline videos downloaded yet",
      noVideosDescription: "When watching any video, click the Download button to save it into your offline vault for data-free playback anytime.",
      exploreVideos: "Explore Videos to Download",
      startDownload: "Start Download",
      chooseResolution: "Choose Resolution",
      fullOfflinePlayback: "Full offline seeking enabled via Service Worker & OPFS byte-range streaming.",
      alreadySaved: "Already saved in your Offline Library",
      readyForPlayback: "Ready for instant playback anytime with zero mobile data.",
      removeFromDevice: "Remove from Device",
      close: "Close",
      cancel: "Cancel",
      confirmClearAll: "Are you sure you want to delete all offline videos from your device?",
      confirmDelete: "Remove this downloaded video from your offline vault?",
      resolution360pDesc: "Low Quality (360p) - Data Saver (~15 MB)",
      resolution480pDesc: "Medium Quality (480p) - Standard (~35 MB)",
      resolution720pDesc: "High Quality (720p) - High Definition (~75 MB)",
    },
    payouts: {
      revenueAndPayouts: "Revenue & Payouts",
      requestPayout: "Request Payout",
      availableBalance: "Available Balance",
      settlementComplete: "Settlement Complete",
      lifetimeEarnings: "Lifetime Earnings",
      minimumThresholdNotice: "Minimum payout: $5 USD or 12,500 CDF",
      configureWallet: "Configure Wallet",
      walletDetails: "Mobile Money & Payout Wallet",
      phoneNumber: "Mobile Money Phone Number",
      accountName: "Account Holder Name",
      provider: "Payment Provider",
      monthlyRunRate: "Monthly Run Rate",
      platformFee: "Platform Infrastructure Fee (20%)",
      netEarnings: "Creator Net Share (80%)",
      pendingPayouts: "Pending Disbursements",
      withdrawAmount: "Withdrawal Amount",
      history: "Settlement History",
      statusPaid: "Paid",
      statusProcessing: "Processing",
      statusFailed: "Failed",
      statusPending: "Pending Review",
      disbursementQueued: "Disbursement queued successfully",
      saveWallet: "Save Wallet Configuration",
      currency: "Currency",
      allRetire: "All (100%)",
    },
    coStreaming: {
      coStreaming: "Go Live Together",
      goLiveTogether: "Go Live Together",
      inviteGuest: "Invite Guest",
      inviteGuestDesc: "Invite a fellow creator or co-host to stream live with you in real-time.",
      copyInviteLink: "Copy Invite Link",
      linkCopied: "Invite link copied to clipboard!",
      waitingInGreenroom: "Waiting in greenroom",
      guestConnected: "Guest Connected",
      sideBySide: "Side-by-Side (50/50)",
      pictureInPicture: "Picture-in-Picture",
      enterStream: "Enter Stream",
      connectingToHost: "Connecting to host broadcast...",
      readyToJoin: "Ready to join stream",
      cameraPreview: "Camera Preview",
      micTest: "Microphone Test",
      audioLevel: "Audio Level",
      muteGuestAudio: "Mute Audio",
      unmuteGuestAudio: "Unmute Audio",
      muteGuestVideo: "Turn Off Video",
      unmuteGuestVideo: "Turn On Video",
      removeGuest: "Remove Guest",
      leaveStream: "Leave Stream",
      guestDisconnected: "Guest has disconnected",
      hostBroadcast: "Host Stream",
      liveSplitView: "Multi-Host Layout",
      guestNameLabel: "Your Display Name",
      guestNamePlaceholder: "Enter your name or handle...",
      onAirBadge: "ON AIR",
      activeGuests: "Active Co-Hosts",
      noGuestsJoined: "No guests joined yet. Copy and share the invite link above.",
    },
    copyright: {
      copyrightMatchTool: "Copyright Match Tool",
      matchesAndClaims: "Matches & Claims",
      registeredAssets: "Registered Assets",
      receivedClaims: "Received Claims",
      registerMasterStem: "Register Master Stem",
      contentIdWarning: "Copyright Claim (Content ID)",
      submitDispute: "Submit Dispute",
      disputeClaim: "Dispute Claim",
      requestTakedown: "Request Takedown",
      monetize: "Monetize",
      track: "Track",
      block: "Block",
      disputeReason: "Dispute Reason",
      disputeExplanation: "Detailed Explanation",
      disputeSubmitted: "Dispute submitted successfully! Review in progress.",
      disputeFairUse: "Fair Use / Transformative Work",
      disputeOriginalCreator: "Original Music / I own 100% of rights",
      disputeLicensed: "Valid Commercial License / Written Permission",
      audioFingerprintMatch: "Audio Fingerprint Match",
      confidence: "Confidence",
      matchedSegment: "Matched Segment",
      releaseClaim: "Release Claim",
      claimReleased: "Claim has been released.",
      noMatchesFound: "No re-upload matches detected for your catalog.",
      noAssetsFound: "No master stems registered yet. Protect your music by adding an asset.",
      noReceivedClaims: "No copyright claims on your uploads. All content is clear.",
      assetTitle: "Asset Title",
      artist: "Artist / Rights Holder",
      isrc: "ISRC Code",
      duration: "Duration (sec)",
      defaultPolicy: "Default Policy",
      registerAssetSuccess: "Master audio stem registered and Content ID acoustic hash generated!",
      filterAll: "All",
      filterActive: "Active",
      filterDisputed: "In Dispute",
      filterResolved: "Resolved / Released",
      statusActive: "Active",
      statusDisputed: "Disputed",
      statusReleased: "Released",
      statusTakedownRequested: "Takedown Requested",
    },
  },
  fr: {
    common: {
      appName: "HP - Helix",
      tagline: "Diffusion vidéo résiliente de nouvelle génération et monétisation directe",
      upload: "Studio de Téléversement",
      player: "Lecteur Vidéo",
      architecture: "Architecture du Pipeline",
      ready: "Prêt",
      processing: "Traitement en cours",
      uploading: "Téléversement...",
      failed: "Échec",
      paused: "En pause",
      cancel: "Annuler",
      pause: "Pause",
      resume: "Reprendre",
      save: "Enregistrer",
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès",
      auto: "Automatique",
    },
    nav: {
      home: "Accueil",
      shorts: "Courts métrages",
      trending: "Tendances",
      subscriptions: "Abonnements",
      history: "Historique",
      offlineLibrary: "Bibliothèque hors-ligne",
      uploadStudio: "Studio de Téléversement",
      studio: "Studio HP-Helix",
      adminPortal: "Portail d'administration",
      architecture: "Architecture Pipeline",
      settings: "Paramètres",
      all: "Tout",
      music: "Musique",
      podcasts: "Podcasts",
      tech: "Technologies",
      entertainment: "Divertissement",
      cinema: "Cinéma",
      africanCinema: "Cinéma",
      gaming: "Jeux vidéo",
      culture: "Culture & Actualités",
      viewChannel: "Voir votre chaîne",
      uploadVideo: "Téléverser une vidéo",
      signOut: "Se déconnecter",
    },
    home: {
      recommendedVideos: "Vidéos recommandées",
      videosAvailable: "{{count}} vidéos disponibles",
      views: "{{count}} vues",
      view: "{{count}} vue",
      hoursAgo: "il y a {{count}} heures",
      daysAgo: "il y a {{count}} jours",
      weeksAgo: "il y a {{count}} semaines",
      justNow: "À l'instant",
      recently: "Récemment",
    },
    studio: {
      uploadVideos: "Téléverser des vidéos",
      dragDropTitle: "Glissez et déposez des fichiers vidéo à téléverser",
      dragDropSubtitle: "Vos vidéos resteront privées jusqu'à leur publication. Prend en charge MP4, WebM, MOV et ProRes haut débit.",
      videoFormat: "Format & Expérience vidéo",
      standardVideo: "Vidéo Standard (16:9)",
      shortReel: "Court métrage / Reel (9:16)",
      relatedVideo: "Vidéo associée",
      relatedVideoDesc: "Liez ce court métrage directement à sa vidéo longue source. Les spectateurs verront un bouton pour accéder directement à la vidéo complète.",
      selectLongVideo: "Sélectionner une vidéo longue",
      noneStandalone: "-- Aucun / Court métrage autonome --",
      jumpTimestamp: "Aller au repère temporel dans la vidéo longue (secondes ou mm:ss)",
      audience: "Audience",
      audienceQuestion: "Cette vidéo est-elle conçue pour les enfants ? (obligatoire)",
      coppaNotice: "Quel que soit votre pays, vous devez respecter la loi sur la protection de la vie privée des enfants en ligne (COPPA).",
      madeForKidsYes: "Oui, elle est conçue pour les enfants",
      madeForKidsNo: "Non, elle n'est pas conçue pour les enfants",
      next: "Suivant",
      back: "Retour",
      publishVideo: "Publier la vidéo",
      publishing: "Publication en cours...",
      uploadCompleteChecksPassed: "Téléversement terminé. Vérifications réussies.",
      channelDashboard: "Tableau de bord de la chaîne",
      welcomeCreator: "Bienvenue dans votre espace créateur, @{{handle}}",
      latestVideoPerformance: "Performances de la dernière vidéo",
      channelAnalytics: "Statistiques de la chaîne",
      currentSubscribers: "Abonnés actuels",
      summaryLast28Days: "Récapitulatif (28 derniers jours)",
      views: "Vues",
      watchTimeHours: "Durée de visionnage (heures)",
      estimatedFanSupport: "Soutien estimé des fans",
      creatorInsightsNews: "Actualités et conseils créateurs",
      uploadVideo: "Téléverser une vidéo",
      askStudio: "Assistant Studio",
      switchToApp: "Retour à l'application",
      edgeTitle: "Diffusion Haute Vitesse Active",
      edgeDescription: "HP-Helix Edge Delivery is serving optimized, low-data streams across Kinshasa, Goma, Lubumbashi, Nairobi, and regional hubs.",
      audienceRetention: "Rétention de l'audience",
      averageViewDuration: "Durée moyenne de visionnage",
      averagePercentageViewed: "Pourcentage moyen visionné",
      retentionSubtitle: "Courbe de rétention sur toute la durée de la vidéo",
      selectVideo: "Sélectionner une vidéo",
      introBenchmark: "Repère d'intro (00:30)",
      introDropoff: "Rétention de l'intro (00:30)",
      introGreat: "Excellente accroche d'intro",
      introTypical: "Abandon typique",
      introNote: "{pct} % des spectateurs regardent encore à 00:30. Une intro accrocheuse limite les abandons.",
      spikesTitle: "Pics et revisionnages",
      spikesNote: "Les pics indiquent les passages revus ou partagés directement avec des amis.",
      rewatch: "Revisionnage",
      ofTotal: "sur {total} au total",
      retentionNoData: "Pas encore assez de données de visionnage pour cette vidéo",
      retentionNoDataHint: "La rétention apparaîtra ici dès que des spectateurs auront regardé cette vidéo.",
      retentionLoading: "Chargement des données de rétention…",
      retentionError: "Impossible de charger les données de rétention.",
      retentionForbidden: "Vous n'avez pas accès aux statistiques de cette vidéo.",
      sidebar: {
        dashboard: "Tableau de bord",
        content: "Contenu",
        playlists: "Playlists",
        analytics: "Statistiques",
        community: "Communauté",
        subtitles: "Sous-titres",
        contentDetection: "Détection de contenu",
        earn: "Monétisation",
        customization: "Personnalisation",
        audioLibrary: "Bibliothèque audio",
        settings: "Paramètres",
        sendFeedback: "Envoyer des commentaires",
      },
      goLive: "Passer au direct",
    },
    watch: {
      createdFrom: "Créé à partir de :",
      subscribe: "S'abonner",
      subscribed: "Abonné",
      like: "J'aime",
      liveChat: "Chat en direct",
      superChat: "Super Chat",
      watchingNow: "spectateurs en direct",
      share: "Partager",
      download: "Télécharger",
      comments: "Commentaires",
      writeComment: "Écrire un commentaire...",
      comment: "Commenter",
      cancel: "Annuler",
      tipCreator: "Merci",
      reply: "Répondre",
      viewReplies: "Afficher les réponses",
      hideReplies: "Masquer les réponses",
      pinnedBy: "Épinglé par",
      pinComment: "Épingler ce commentaire",
      unpinComment: "Désépingler",
    },
    tipping: {
      tipCreator: "Merci",
      merci: "Merci",
      modalTitle: "Soutenir le créateur",
      modalSubtitle: "Soutien direct par Mobile Money via Airtel Money, Vodacom M-Pesa ou Orange Money",
      currency: "Devise",
      presetAmounts: "Montants prédéfinis",
      customAmount: "Montant personnalisé",
      selectProvider: "Choisir l'opérateur Mobile Money",
      phoneNumber: "Numéro de téléphone Mobile Money",
      phoneNumberPlaceholder: "81 234 5678",
      message: "Message de soutien",
      messagePlaceholder: "Laissez un mot d'encouragement (optionnel)...",
      sendTip: "Envoyer le pourboire",
      sending: "Traitement Mobile Money...",
      tipSuccess: "Merci pour votre soutien !",
      tipSuccessDesc: "Votre pourboire a été transféré directement au créateur.",
      tippedBadge: "A pourboiré",
      superThanks: "Super Merci",
      feeBreakdown: "80% va directement au créateur, 20% de frais d'infrastructure de la plateforme",
    },
    admin: {
      dashboardTitle: "Portail d'administration Global",
      operationsTitle: "Opérations et modération de la plateforme",
      totalVolume: "Volume Total",
      platformMargin: "Marge de la Plateforme (20%)",
      creatorNet: "Net Créateur (80%)",
      catalogHealth: "Santé du Catalogue",
      moderationTab: "File de modération IA",
      payoutsTab: "File des paiements créateurs",
      tableType: "Type",
      tableContent: "Contenu / Titre",
      tableAuthor: "Auteur",
      tableReason: "Motif de signalement",
      tableScore: "Score de toxicité",
      tableStatus: "Statut",
      tableActions: "Actions",
      tableRequestId: "ID Demande",
      tableCreator: "Créateur / Chaîne",
      tableAmount: "Montant",
      tableMethod: "Méthode de paiement",
      tablePhone: "Numéro de téléphone",
      approve: "Approuver",
      reject: "Supprimer / Rejeter",
      processPayout: "Traiter le paiement",
      pageNotAvailable: "Cette page n'est pas disponible",
      pageNotFoundDesc: "Le lien que vous avez suivi est peut-être rompu, ou la page a été supprimée ou n'existe pas.",
      commandCenter: "Centre de commande",
      switchboard: "Tableau de bord système",
      systemLogs: "Journaux d'audit",
      concurrentViewers: "Spectateurs simultanés",
      activeBroadcasts: "Directs en cours",
      bandwidthEgress: "Bande passante sortante (24h)",
      edgeCacheHitRate: "Taux de succès Edge Cache",
      momoGmv30d: "Volume Mobile Money (30j)",
      pendingModeration: "Modération en attente",
      pendingDisputes: "Litiges en attente",
      pendingPayouts: "Paiements en attente",
      regionalConcentration: "Concentration géographique",
      emergencyMaintenance: "Mode maintenance d'urgence",
      emergencyMaintenanceDesc: "Verrouille immédiatement la plateforme publique derrière la page de maintenance.",
      registrationLock: "Verrouillage des inscriptions",
      registrationLockDesc: "Suspend temporairement la création de nouvelles chaînes créateurs.",
      payoutEngine: "Moteur de paiement Mobile Money",
      payoutEngineDesc: "Active les décaissements automatisés par lots vers les portefeuilles MoMo.",
      highBitrateCap: "Plafond de débit (720p)",
      highBitrateCapDesc: "Limite le flux à 720p maximum lors des coupures de fibre sous-marine régionales.",
      confirmToggleTitle: "Confirmer la modification système",
      confirmToggleDesc: "Cette action s'applique immédiatement à tous les serveurs Edge et sera inscrite dans le journal d'audit.",
      confirmAction: "Confirmer & Appliquer",
      cancelAction: "Annuler",
      auditTrail: "Piste d'audit immuable",
      actionFilter: "Filtrer par action",
      entityFilter: "Filtrer par entité",
      appliedBy: "Exécuté par",
      timestamp: "Horodatage",
      statusHealthy: "Opérationnel / Normal",
    },
    upload: {
      pageTitle: "Studio de Téléversement Créateur",
      pageSubtitle: "Téléversements directs et reprenables vers Cloudflare Stream via le protocole TUS",
      directUploadNotice: "Zéro mise en mémoire tampon : Vos vidéos sont envoyées directement depuis votre navigateur vers les serveurs Cloudflare.",
      dragDropText: "Glissez et déposez votre fichier vidéo ici",
      orBrowse: "ou cliquez pour sélectionner un fichier",
      supportedFormats: "Formats pris en charge : MP4, MOV, MKV, WebM jusqu'à 30 Go",
      selectedFile: "Fichier sélectionné",
      fileSize: "Taille du fichier",
      titleLabel: "Titre de la vidéo",
      titlePlaceholder: "Entrez un titre captivant...",
      descLabel: "Description",
      descPlaceholder: "Détails, crédits ou liens pour votre public...",
      categoryLabel: "Catégorie",
      languageLabel: "Langue principale / Audio",
      creatorProfileIdLabel: "Identifiant du Profil Créateur",
      creatorProfileIdPlaceholder: "ex : clxyz123abc ou votre ID créateur",
      maxDurationLabel: "Durée maximale (secondes)",
      startUploadButton: "Démarrer le téléversement direct vers Cloudflare Stream",
      uploadProgress: "Progression du téléversement",
      uploadSpeed: "Vitesse",
      eta: "Temps restant estimé",
      uploadedBytes: "Transféré",
      tusResumableTag: "Protocole TUS : Transfert reprenable actif",
      sessionCreated: "Session authentifiée créée sur Cloudflare Stream",
      uploadSuccessTitle: "Téléversement terminé avec succès !",
      uploadSuccessDesc: "Votre vidéo est arrivée sur Cloudflare Stream et le transcodage HLS multi-débit commence.",
      streamIdLabel: "Identifiant Média Stream (UID)",
      hlsUrlLabel: "URL du Manifeste HLS Adaptatif",
      watchPreviewButton: "Aperçu dans le Lecteur Adaptatif",
      uploadAnotherButton: "Téléverser une autre vidéo",
      errors: {
        noFile: "Veuillez sélectionner un fichier vidéo à téléverser.",
        noTitle: "Le titre de la vidéo est obligatoire.",
        noCreatorId: "L'identifiant du profil créateur est obligatoire.",
        sessionFailed: "Impossible d'obtenir l'URL de téléversement direct depuis le backend.",
        uploadFailed: "Le téléversement direct TUS a rencontré une erreur.",
      },
    },
    categories: {
      music: "Musique & Spectacle",
      education: "Éducation & Tutoriels",
      gaming: "Jeux vidéo & Esports",
      entertainment: "Divertissement & Humour",
      cultureNews: "Culture & Documentaires",
      technology: "Technologie & Programmation",
      lifestyle: "Style de vie & Vlogs",
    },
    player: {
      pageTitle: "Lecteur HLS Adaptatif",
      pageSubtitle: "Lecture vidéo multi-débit à faible latence avec sélecteur de qualité et téléchargement hors-ligne",
      enterHlsOrUid: "Rechercher des vidéos, artistes, podcasts...",
      loadVideo: "Charger le flux",
      quality: "Qualité",
      speed: "Vitesse de lecture",
      normal: "Normale",
      sourceLocked: "Source verrouillée",
      downloadOffline: "Télécharger MP4 pour visionnage hors-ligne",
      downloadMp4Notice: "Téléchargement direct du fichier MP4 source Cloudflare Stream",
      downloading: "Préparation du téléchargement...",
      currentResolution: "Résolution actuelle",
      bitrate: "Débit binaire",
      liveStats: "Télémétrie du flux",
      showStats: "Afficher les statistiques",
      hideStats: "Masquer les statistiques",
      pip: "Image dans l'image",
      fullscreen: "Plein écran",
      mute: "Couper le son",
      unmute: "Activer le son",
      play: "Lecture",
      pause: "Pause",
      buffering: "Mise en mémoire tampon...",
      streamMediaId: "Identifiant Stream (UID)",
      demoVideoTitle: "Vidéo de démonstration HLS Adaptatif Helix",
      demoVideoCreator: "Moteur de diffusion HP - Helix",
      chapters: "Chapitres",
      chapter: "Chapitre",
      autoChapters: "Activer les chapitres automatiques de la description",
      endScreen: "Écran de fin",
      subscribe: "S'abonner",
      subscribed: "Abonné",
      watchNext: "À regarder ensuite",
      audioTrack: "Piste audio",
      originalAudio: "Audio d'origine",
      dubbedAudio: "Piste doublée",
      audioTrackDesc: "Pistes audio multilingues",
      addAudioDub: "Ajouter un doublage audio",
    },
    arch: {
      title: "Architecture Cloudflare Stream + Render Invariante",
      subtitle: "Architecture haute performance sans aucune saturation disque ou mémoire sur le backend Render.",
      zeroBackendRuleTitle: "Règle fondamentale : Zéro transit vidéo sur le backend",
      zeroBackendRuleDesc: "Les binaires vidéo des clients ne transitent jamais par le serveur Fastify Render. Tous les téléversements vont directement du navigateur vers Cloudflare Stream via des URLs TUS sécurisées.",
      pipelineSteps: {
        step1Title: "1. Session directe authentifiée",
        step1Desc: "Le client demande une session directe à l'API Fastify. Le backend génère un jeton TUS auprès de Cloudflare et enregistre la vidéo.",
        step2Title: "2. Téléversement par morceaux (TUS)",
        step2Desc: "Le client transfère les fragments vidéo directement aux nœuds Cloudflare avec reprise automatique en cas d'interruption.",
        step3Title: "3. Transcodage HLS multi-résolution",
        step3Desc: "Cloudflare Stream encode automatiquement en 1080p, 720p, 480p, 360p, 240p et 144p.",
        step4Title: "4. Synchronisation Webhook & Diffusion Mondiale",
        step4Desc: "Cloudflare notifie le serveur Render dès que la vidéo est prête pour la diffusion mondiale et le téléchargement MP4.",
      },
    },
    notifications: {
      title: "Notifications",
      all: "Toutes",
      personalized: "Personnalisées",
      none: "Aucune",
      markAllAsRead: "Tout marquer comme lu",
      empty: "Aucune nouvelle notification",
      newUpload: "Nouveau téléversement",
      tierUpdated: "Préférence de notification mise à jour",
    },
    community: {
      community: "Communauté",
      postsAndPolls: "Publications & Sondages",
      createPost: "Créer une publication",
      postPlaceholder: "Partagez une nouvelle avec votre communauté ou posez une question...",
      addPoll: "Ajouter un sondage",
      removePoll: "Supprimer le sondage",
      pollOptions: "Options du sondage (2 à 5)",
      option: "Option",
      addOption: "Ajouter une option",
      public: "Public",
      membersOnly: "Réservé aux membres",
      publish: "Publier",
      publishing: "Publication en cours...",
      postPublished: "Publication publiée avec succès !",
      contentRequired: "Veuillez saisir du contenu pour votre publication.",
      pollMinOptions: "Un sondage nécessite au moins 2 options valides.",
      publishedPosts: "Publications publiées",
      noPosts: "Aucune publication communautaire pour le moment",
      noPostsDesc: "Lorsque ce créateur publiera des actualités ou des sondages, ils apparaîtront ici.",
      deletePost: "Supprimer la publication",
      likes: "j'aime",
      vote: "Voter",
      votesCount: "votes",
      selectToVote: "Sélectionnez une option pour voter",
      clickToChangeVote: "Cliquez sur une option pour modifier votre vote",
      loadingPosts: "Chargement des publications communautaires...",
    },
    verification: {
      title: "Badges de Chaîne & Vérification",
      badgeVerified: "Créateur Vérifié",
      badgeOfficialArtist: "Artiste Officiel",
      badgePartner: "Partenaire Vérifié",
      eligibilityTitle: "Baromètre d'Éligibilité",
      applyButton: "Demander la Vérification",
      statusPending: "Demande en cours d'examen",
      statusApproved: "Vérifié",
      statusRejected: "Demande rejetée",
    },
    pwa: {
      installApp: "Installer l'Application HP-Helix",
      installDesc: "Profitez d'une lecture ultra fluide, de téléchargements hors-ligne et d'une expérience native sur votre appareil.",
      installButton: "Installer l'App",
      dismiss: "Plus tard",
      pushTitle: "Notifications Push sur l'Appareil",
      pushDesc: "Recevez des alertes en temps réel lorsque vos créateurs favoris publient de nouvelles vidéos ou lancent un direct.",
      enablePush: "Activer les Notifications",
      disablePush: "Désactiver les Notifications",
      sendTestPush: "Envoyer une Notification Test",
      testPushSuccess: "Notification test envoyée à votre navigateur !",
      permissionBlocked: "Notifications bloquées. Veuillez accorder l'autorisation dans les paramètres de votre navigateur.",
      installedBanner: "HP-Helix est installée et prête pour une utilisation hors ligne.",
    },
    experiments: {
      testAndCompare: "Tester et comparer",
      testAndCompareDesc: "Testez jusqu'à 3 miniatures et titres alternatifs auprès de votre audience. L'algorithme optimise le temps de visionnage ou le taux de clics.",
      startTest: "Lancer le test A/B",
      stopTest: "Arrêter le test",
      pickWinner: "Choisir comme gagnant",
      winningVariant: "Variante gagnante",
      leading: "En tête",
      slotA: "Option A (Originale)",
      slotB: "Option B (Variante 2)",
      slotC: "Option C (Variante 3)",
      addSlotC: "+ Ajouter une 3e miniature (Optionnel)",
      removeSlotC: "Supprimer l'option C",
      metricLabel: "Métrique d'optimisation",
      metricWatchTime: "Part du temps de visionnage (Recommandé pour la portée algorithmique)",
      metricCtr: "Taux de clics (CTR)",
      impressions: "Impressions",
      clicks: "Clics",
      ctrRate: "CTR",
      watchTimeShare: "Part du temps de visionnage",
      runningTest: "Test en cours",
      completedTest: "Test terminé",
      stoppedTest: "Test arrêté",
      alternateTitle: "Titre alternatif (Optionnel)",
      noActiveTest: "Aucun test actif en cours pour cette vidéo.",
      winnerPromotedSuccess: "Variante promue avec succès en miniature et titre permanents !",
    },
    memberships: {
      join: "Rejoindre",
      memberships: "Abonnements",
      membersOnly: "Réservé aux membres",
      membershipTiers: "Niveaux d'abonnement",
      joinChannel: "Rejoindre la chaîne",
      joinDescription: "Débloquez des avantages exclusifs, des badges de fidélité et un accès direct au créateur.",
      monthlyPerks: "Avantages mensuels",
      loyaltyBadge: "Badge de fidélité",
      chooseTier: "Choisissez votre niveau",
      momoPayment: "Sélectionnez l'opérateur Mobile Money",
      activeMembers: "Membres actifs",
      monthlyRevenue: "Revenu mensuel récurrent (MRR)",
      createTier: "Ajouter un niveau",
      tierName: "Nom du niveau",
      priceUsd: "Prix mensuel ($ USD)",
      priceCdf: "Prix mensuel (CDF)",
      perks: "Avantages",
      subscribedSince: "Membre depuis",
      monthsMember: "mois de fidélité",
    },
    premieres: {
      premiere: "Première",
      livePremiere: "Première en direct",
      premiereStartsIn: "La première commence dans",
      remindMe: "M'avertir",
      reminderSet: "Rappel activé",
      premiereCountdown: "Compte à rebours de première",
      setAsPremiere: "Programmer une première",
      waitingRoomNotice: "Salle d'attente active. Discutez avec les autres spectateurs avant le début.",
      joinLiveChat: "Rejoindre le chat en direct",
    },
    recommendations: {
      upNext: "À suivre",
      autoplayOn: "Lecture automatique activée",
      autoplayOff: "Lecture automatique désactivée",
      cancel: "Annuler",
      playNow: "Lire maintenant",
      regionalTrending: "Tendance en RDC et Afrique de l'Est",
      dataSaverActive: "Économiseur de données actif",
      similarCategory: "Catégorie similaire",
      fromFavoriteChannel: "De chaînes que vous regardez",
    },
    maintenance: {
      headline: "HP-Helix Video Streaming | Bientôt Disponible",
      subtitle: "La plateforme vidéo pour la RDC et l'Afrique régionale est actuellement en phase de test privé.",
      badge: "En Construction",
      devAccess: "Accès Développeur / Creator Early Access",
      devModalTitle: "Accès Anticipé Développeur & Créateur",
      devModalSubtitle: "Entrez la clé d'accès alpha pour déverrouiller l'expérience complète de la plateforme.",
      passcodePlaceholder: "Entrez la clé d'accès (ex: helix_alpha_2026)...",
      unlockButton: "Déverrouiller la plateforme",
      unlocking: "Déverrouillage...",
      invalidPasscode: "Clé d'accès invalide. Veuillez vérifier vos identifiants.",
      featureHlsTitle: "Diffusion 4K HLS Ultra-Rapide",
      featureHlsDesc: "Ingestion directe Cloudflare Stream CDN sans transit binaire sur le serveur.",
      featureMobileTitle: "Économiseur de Données Mobile",
      featureMobileDesc: "Optimisé pour la RDC et l'Afrique de l'Est avec mise en cache hors-ligne.",
      featureMonetizationTitle: "Rémunération Directe Créateurs",
      featureMonetizationDesc: "Intégration Mobile Money instantanée pour M-Pesa, Airtel Money et Orange Money.",
      featureChatTitle: "Premières & Chat en Direct",
      featureChatDesc: "Comptes à rebours synchronisés et salon de discussion en temps réel.",
    },
    clips: {
      clip: "Extrait",
      createClip: "Créer un extrait",
      watchFullVideo: "Regarder la vidéo complète",
      shareClip: "Partager l'extrait",
      clipDuration: "Durée de l'extrait",
      titlePlaceholder: "Ajouter un titre à votre extrait (ex: Meilleur refrain)...",
      titleRequired: "Le titre est requis (100 caractères max)",
      characterCount: "caractères",
      previewLoop: "Aperçu en boucle",
      copiedLink: "Lien de l'extrait copié dans le presse-papiers !",
      copyLink: "Copier le lien",
      shareWhatsApp: "Partager sur WhatsApp",
      shareX: "Partager sur X",
      shareFacebook: "Partager sur Facebook",
      minDurationError: "L'extrait doit durer au moins 5 secondes.",
      maxDurationError: "L'extrait ne peut pas dépasser 60 secondes.",
      creating: "Création de l'extrait...",
      viewClip: "Voir l'extrait",
      viewsCount: "vues",
      startTime: "Début",
      endTime: "Fin",
    },
    studioUpload: {
      uploadVideo: "Mettre en ligne une vidéo",
      selectFromVideoFrames: "Choisir parmi les images",
      uploading: "Envoi en cours...",
      standardVideoBadge: "Vidéo Standard (16:9)",
      shortBadge: "Court Helix (9:16)",
      customThumbnail: "Miniature personnalisée",
      uploadCustomThumbnail: "Importer une miniature",
      uploadSpeed: "Vitesse de transfert",
      timeRemaining: "Temps restant",
      pauseUpload: "Mettre en pause",
      resumeUpload: "Reprendre",
      cancelUpload: "Annuler",
      visibilityLabel: "Visibilité",
      monetizationOptIn: "Activer l'accès payant & la monétisation Mobile Money",
      mobileMoneyPayouts: "Paiements Airtel Money / M-Pesa / Orange Money",
      titleAutoPopulated: "Titre généré automatiquement à partir du fichier",
      probingMedia: "Analyse du format & extraction des miniatures...",
    },
    merch: {
      store: "Boutique",
      merchandise: "Articles officiels",
      officialMerch: "Articles officiels",
      buyNow: "Acheter maintenant",
      order: "Commander",
      inStock: "En stock",
      outOfStock: "Rupture de stock",
      deliveryCity: "Ville de livraison",
      deliveryAddress: "Adresse de livraison",
      buyerName: "Nom et prénom",
      buyerPhone: "Numéro Mobile Money (M-Pesa / Airtel / Orange)",
      paymentMethod: "Mode de paiement",
      momoPaymentPrompt: "Une demande de confirmation Mobile Money sera envoyée sur votre téléphone.",
      placeOrder: "Confirmer la commande avec Mobile Money",
      placingOrder: "Envoi de la demande...",
      orderSuccess: "Commande confirmée !",
      orderSuccessDesc: "Veuillez valider le prompt Mobile Money avec votre code secret sur votre téléphone.",
      pinnedMerch: "Produits officiels du créateur",
      allProducts: "Tous les articles",
      apparel: "Vêtements",
      musicVinyl: "Musique & Vinyles",
      accessories: "Accessoires",
      close: "Fermer",
    },
    moderation: {
      heldForReview: "Mis en attente d'examen",
      published: "Publiés",
      reportContent: "Signaler le contenu",
      reportSuccess: "Merci pour votre signalement. Ce contenu est en cours d'examen.",
      approve: "Approuver",
      remove: "Supprimer",
      blockAuthor: "Bloquer l'auteur",
      approveAll: "Tout approuver",
      removeAll: "Tout supprimer",
      commentsQueue: "File d'attente de modération des commentaires",
      noHeldComments: "Aucun commentaire en attente d'examen.",
      noPublishedComments: "Aucun commentaire publié pour le moment.",
      selectAll: "Tout sélectionner",
      selectedCount: "sélectionné(s)",
      reportReason: "Sélectionnez le motif du signalement",
      reasonSpam: "Contenu trompeur ou spam",
      reasonHateSpeech: "Incitation à la haine ou harcèlement",
      reasonCopyright: "Violation de droits d'auteur",
      reasonExplicit: "Contenu explicite ou violent",
      additionalDetails: "Détails complémentaires (facultatif)",
      detailsPlaceholder: "Apportez des précisions pour aider l'équipe de modération...",
      submitReport: "Envoyer le signalement",
      cancel: "Annuler",
      authorBlockedNotice: "L'auteur a été bloqué pour cette chaîne.",
      commentApprovedNotice: "Commentaire approuvé et publié.",
      commentRemovedNotice: "Commentaire supprimé.",
    },
    live: {
      live: "EN DIRECT",
      liveBadge: "EN DIRECT",
      rewindToLive: "Revenir au direct",
      streamEnded: "Le direct est terminé",
      enableDvr: "Activer la fenêtre DVR (Retour jusqu'à 2 heures)",
      enableDvrDesc: "Permet aux spectateurs de mettre en pause et remonter le direct.",
      autoPublishVod: "Publier automatiquement en rediffusion VOD",
      autoPublishVodDesc: "Convertit automatiquement la diffusion en vidéo permanente à la fin.",
      liveReplay: "Rediffusion en direct",
      goToLive: "Revenir au direct",
      liveStream: "Diffusion en direct",
    },
    playlists: {
      playlists: "Playlists",
      playlist: "Playlist",
      saveToPlaylist: "Enregistrer",
      createPlaylist: "Créer une playlist",
      playlistTitle: "Titre de la playlist",
      privacy: "Confidentialité",
      public: "Publique",
      unlisted: "Non répertoriée",
      private: "Privée",
      shuffle: "Lecture aléatoire",
      loop: "Répéter la playlist",
      loopOn: "Répéter la playlist : ACTIVÉ",
      loopOff: "Répéter la playlist : DÉSACTIVÉ",
      mix: "Mix HP-Helix",
      mixDesc: "Mix intelligent basé sur cette vidéo",
      videoCount: "vidéos",
      emptyPlaylist: "Aucune playlist trouvée. Créez-en une ci-dessous !",
    },
    panoramic: {
      panoramicVideo: "Vidéo 360°",
      vrMode: "Mode VR",
      cardboardMode: "Mode VR Cardboard",
      dragToLook: "Faites glisser pour regarder",
      tiltDevice: "Inclinez l'appareil pour regarder",
      resetBearing: "Réinitialiser vers le Nord",
      compass: "Boussole",
      projectionEquirectangular: "Équiréctangulaire (Sphère 360°)",
      projectionFlat: "Projection plane",
      recordedIn360: "Cette vidéo a été enregistrée en 360° / VR",
      recordedIn360Desc: "Activez le lecteur panoramique 360° et la navigation spatiale pour cette vidéo.",
    },
    offlineVault: {
      download: "Télécharger",
      downloading: "Téléchargement en cours...",
      downloaded: "Téléchargé",
      offlineLibrary: "Bibliothèque hors-ligne",
      storage: "Stockage",
      storageUsed: "Stockage utilisé",
      clearAll: "Tout effacer",
      deleteFromDevice: "Supprimer de l'appareil",
      dataSaver: "Économiseur de données (360p)",
      standardQuality: "Standard (480p)",
      highDefinition: "Haute définition (720p)",
      offlineModeActive: "Mode hors-ligne actif",
      offlineModeNotice: "Mode hors ligne actif - Lecture depuis votre bibliothèque locale",
      noVideosDownloaded: "Aucune vidéo hors-ligne enregistrée",
      noVideosDescription: "Pendant la lecture, cliquez sur Télécharger pour sauvegarder la vidéo dans votre coffre hors-ligne sans consommer de données.",
      exploreVideos: "Explorer des vidéos à télécharger",
      startDownload: "Démarrer le téléchargement",
      chooseResolution: "Choisir la résolution",
      fullOfflinePlayback: "Navigation hors-ligne fluide activée via Service Worker et streaming par plage d'octets OPFS.",
      alreadySaved: "Déjà enregistré dans votre bibliothèque hors-ligne",
      readyForPlayback: "Prêt pour une lecture instantanée sans données mobiles.",
      removeFromDevice: "Supprimer de l'appareil",
      close: "Fermer",
      cancel: "Annuler",
      confirmClearAll: "Êtes-vous sûr de vouloir supprimer toutes les vidéos hors-ligne de votre appareil ?",
      confirmDelete: "Supprimer cette vidéo téléchargée de votre coffre hors-ligne ?",
      resolution360pDesc: "Qualité faible (360p) - Économiseur de données (~15 MB)",
      resolution480pDesc: "Qualité moyenne (480p) - Standard (~35 MB)",
      resolution720pDesc: "Haute qualité (720p) - Haute définition (~75 MB)",
    },
    payouts: {
      revenueAndPayouts: "Revenus & Paiements",
      requestPayout: "Demander un retrait",
      availableBalance: "Solde disponible",
      settlementComplete: "Paiement envoyé",
      lifetimeEarnings: "Revenus cumulés",
      minimumThresholdNotice: "Retrait minimum : 5 $ USD ou 12 500 CDF",
      configureWallet: "Configurer le portefeuille",
      walletDetails: "Portefeuille Mobile Money & Retraits",
      phoneNumber: "Numéro de téléphone Mobile Money",
      accountName: "Nom du titulaire du compte",
      provider: "Opérateur de paiement",
      monthlyRunRate: "Estimation mensuelle",
      platformFee: "Frais de plateforme (20%)",
      netEarnings: "Part nette créateur (80%)",
      pendingPayouts: "Retraits en cours",
      withdrawAmount: "Montant à retirer",
      history: "Historique des paiements",
      statusPaid: "Payé",
      statusProcessing: "En cours",
      statusFailed: "Échoué",
      statusPending: "En attente",
      disbursementQueued: "Demande de retrait transmise avec succès",
      saveWallet: "Enregistrer le portefeuille",
      currency: "Devise",
      allRetire: "Tout retirer (100%)",
    },
    coStreaming: {
      coStreaming: "Diffuser à deux",
      goLiveTogether: "Diffuser à deux",
      inviteGuest: "Inviter un participant",
      inviteGuestDesc: "Invitez un autre créateur ou co-animateur à diffuser en direct avec vous.",
      copyInviteLink: "Copier le lien d'invitation",
      linkCopied: "Lien d'invitation copié dans le presse-papiers !",
      waitingInGreenroom: "En salle d'attente",
      guestConnected: "Participant connecté",
      sideBySide: "Côte à côte (50/50)",
      pictureInPicture: "Incrustation d'image (PiP)",
      enterStream: "Rejoindre le direct",
      connectingToHost: "Connexion à la diffusion de l'hôte...",
      readyToJoin: "Prêt à entrer en direct",
      cameraPreview: "Aperçu de la caméra",
      micTest: "Test du micro",
      audioLevel: "Niveau audio",
      muteGuestAudio: "Couper le micro",
      unmuteGuestAudio: "Activer le micro",
      muteGuestVideo: "Couper la caméra",
      unmuteGuestVideo: "Activer la caméra",
      removeGuest: "Retirer du direct",
      leaveStream: "Quitter le direct",
      guestDisconnected: "Le participant s'est déconnecté",
      hostBroadcast: "Flux de l'hôte",
      liveSplitView: "Mise en page multi-hôtes",
      guestNameLabel: "Votre nom d'affichage",
      guestNamePlaceholder: "Entrez votre nom ou pseudonyme...",
      onAirBadge: "EN DIRECT",
      activeGuests: "Co-animateurs actifs",
      noGuestsJoined: "Aucun participant pour le moment. Copiez et partagez le lien ci-dessus.",
    },
    copyright: {
      copyrightMatchTool: "Outil Copyright Match",
      matchesAndClaims: "Correspondances & Réclamations",
      registeredAssets: "Œuvres Enregistrées",
      receivedClaims: "Réclamations Reçues",
      registerMasterStem: "Enregistrer un Master / Stem",
      contentIdWarning: "Réclamation pour atteinte aux droits d'auteur (Content ID)",
      submitDispute: "Contester la réclamation",
      disputeClaim: "Contester le Content ID",
      requestTakedown: "Demander la suppression",
      monetize: "Monétiser",
      track: "Suivre les stats",
      block: "Bloquer",
      disputeReason: "Motif de la contestation",
      disputeExplanation: "Explication détaillée",
      disputeSubmitted: "Contestation transmise avec succès ! Examen en cours.",
      disputeFairUse: "Usage loyal (Fair Use) / Œuvre transformative",
      disputeOriginalCreator: "Musique originale / Je détiens 100% des droits",
      disputeLicensed: "Licence commerciale valide / Autorisation écrite",
      audioFingerprintMatch: "Empreinte acoustique concordante",
      confidence: "Indice de confiance",
      matchedSegment: "Segment sonore identifié",
      releaseClaim: "Lever la réclamation",
      claimReleased: "La réclamation a été levée avec succès.",
      noMatchesFound: "Aucune réutilisation détectée pour votre catalogue.",
      noAssetsFound: "Aucun master stem enregistré. Protégez votre musique en ajoutant un asset.",
      noReceivedClaims: "Aucune réclamation sur vos vidéos. Tout votre contenu est sain.",
      assetTitle: "Titre de l'œuvre",
      artist: "Artiste / Ayant droit",
      isrc: "Code ISRC",
      duration: "Durée (secondes)",
      defaultPolicy: "Politique par défaut",
      registerAssetSuccess: "Master stem enregistré et empreinte acoustique Content ID générée !",
      filterAll: "Tous",
      filterActive: "Actifs",
      filterDisputed: "En litige",
      filterResolved: "Résolus / Levés",
      statusActive: "Actif",
      statusDisputed: "En litige",
      statusReleased: "Levé",
      statusTakedownRequested: "Suppression demandée",
    },
  },
  ln: {
    common: {
      appName: "HP - Helix",
      tagline: "Botalisi video ya sika mpe bozwi mbongo mbala moko mpo na basali video",
      upload: "Esika ya Kotinda Video",
      player: "Molakisi Video",
      architecture: "Ndengé Sistɛmɛ Esalaka",
      ready: "Ebongisami",
      processing: "Ezali kobongisama",
      uploading: "Ezali kotinda...",
      failed: "Ebebi",
      paused: "Epɛngwami",
      cancel: "Tika",
      pause: "Tɛlɛmisa",
      resume: "Koba",
      save: "Bomba",
      loading: "Ezali kokota...",
      error: "Mbeba",
      success: "Elongi",
      auto: "Auto",
    },
    nav: {
      home: "Ndako",
      shorts: "Bililingi ya Mokuse",
      trending: "Ezo Pela",
      subscriptions: "Bato Olandaka",
      history: "Masolo ya Kala",
      offlineLibrary: "Bibiyotɛkɛ ya nse ya internet",
      uploadStudio: "Esika ya Kotinda",
      studio: "Studio HP-Helix",
      adminPortal: "Esika ya Mokambi",
      architecture: "Ndengé Sistɛmɛ Esalaka",
      settings: "Bongisa",
      all: "Nionso",
      music: "Miziki",
      podcasts: "Masolo (Podcasts)",
      tech: "Teknolozi",
      entertainment: "Kosekisa",
      cinema: "Sinema",
      africanCinema: "Sinema",
      gaming: "Masano",
      culture: "Mimeseno & Sango",
      viewChannel: "Tala shene na yo",
      uploadVideo: "Kotinda video",
      signOut: "Bima",
    },
    home: {
      recommendedVideos: "Bavidéo baponeli yo",
      videosAvailable: "Bavidéo {{count}} ezali",
      views: "{{count}} kotala",
      view: "{{count}} kotala",
      hoursAgo: "eleki ngonga {{count}}",
      daysAgo: "eleki mikolo {{count}}",
      weeksAgo: "eleki baposo {{count}}",
      justNow: "Sik'oyo",
      recently: "Kala mingi te",
    },
    studio: {
      uploadVideos: "Kotinda bavidéo",
      dragDropTitle: "Benda mpe tika bafishi ya video mpo na kotinda",
      dragDropSubtitle: "Bavidéo na yo ekozala ya sekele kino okobimisa yango. Ezali koyamba MP4, WebM, MOV mpe ProRes.",
      videoFormat: "Lolenge ya Video & Botalisi",
      standardVideo: "Video ya Mombimba (16:9)",
      shortReel: "Video ya Mokuse / Reel (9:16)",
      relatedVideo: "Video etali yango",
      relatedVideoDesc: "Kanga video ya mokuse oyo mbala moko na video ya molai oyo euti na yango. Batalisi bakomona bouton mpo na kotala video mobimba.",
      selectLongVideo: "Pona Video ya Molai",
      noneStandalone: "-- Moko te / Video ya mokuse kaka --",
      jumpTimestamp: "Kende na ntango ya video ya molai (segonde to mm:ss)",
      audience: "Batalisi",
      audienceQuestion: "Video oyo ezali mpo na bana mike? (esengeli)",
      coppaNotice: "Ata esika ozali, mobeko esengi otosa mibeko ya kobatela bana na internet (COPPA).",
      madeForKidsYes: "Iyo, ezali mpo na bana mike",
      madeForKidsNo: "Te, ezali mpo na bana mike te",
      next: "Kende liboso",
      back: "Zonga sima",
      publishVideo: "Bimisa Video",
      publishing: "Ezali kobimisa...",
      uploadCompleteChecksPassed: "Kotinda esili. Bopeto elongi.",
      channelDashboard: "Tableau de bord ya shene",
      welcomeCreator: "Boyeyi malamu na esika na yo ya mosali, @{{handle}}",
      latestVideoPerformance: "Botalisi ya video ya suka",
      channelAnalytics: "Statistiques ya shene",
      currentSubscribers: "Bato bazali kolanda sik'oyo",
      summaryLast28Days: "Bokuse (Mikolo 28 ya suka)",
      views: "Kotala",
      watchTimeHours: "Ntango ya kotala (ngonga)",
      estimatedFanSupport: "Lisungi ya bafana",
      creatorInsightsNews: "Sango mpe toli mpo na basali",
      uploadVideo: "Kotinda video",
      askStudio: "Mosungi Studio",
      switchToApp: "Zonga na application",
      edgeTitle: "Diffusion Haute Vitesse Active",
      edgeDescription: "HP-Helix Edge Delivery is serving optimized, low-data streams across Kinshasa, Goma, Lubumbashi, Nairobi, and regional hubs.",
      audienceRetention: "Babateli ya kotala",
      averageViewDuration: "Moyene ya tango ya kotala",
      averagePercentageViewed: "Moyene ya bopanzami",
      retentionSubtitle: "Molongo ya bato oyo bazali kotala video tii na suka",
      selectVideo: "Positola video",
      introBenchmark: "Elembo ya ebandeli (00:30)",
      introDropoff: "Bato oyo batikali na 00:30",
      introGreat: "Ebandeli ezali kobenga bato malamu",
      introTypical: "Kokita ya ndenge ya kawaida",
      introNote: "{pct}% ya bato bazali naino kotala na 00:30. Ebandeli ya makasi esalisaka bato bátikala.",
      spikesTitle: "Bisika etalamaki lisusu",
      spikesNote: "Bisika ya likolo elakisi biteni oyo bato batalaki lisusu to batindelaki baninga.",
      rewatch: "Etalamaki lisusu",
      ofTotal: "na {total} mobimba",
      retentionNoData: "Ezali naino na ndimbola ya kotala mingi te",
      retentionNoDataHint: "Okomona yango awa bato bakotala video oyo.",
      retentionLoading: "Tozali kozwa ndimbola…",
      retentionError: "Tokoki kozwa ndimbola te.",
      retentionForbidden: "Ozali na ndingisa te ya komona ba statistiques ya video oyo.",
      sidebar: {
        dashboard: "Tableau de bord",
        content: "Biloko na yo",
        playlists: "Ba playlists",
        analytics: "Statistiques",
        community: "Lisanga",
        subtitles: "Makomi na se (Sous-titres)",
        contentDetection: "Kososola biloko",
        earn: "Kozwa mbongo",
        customization: "Kobongisa shene",
        audioLibrary: "Esika ya miziki",
        settings: "Bongisa",
        sendFeedback: "Tinda makanisi",
      },
      goLive: "Banda live",
    },
    watch: {
      createdFrom: "Euti na:",
      subscribe: "Landa",
      subscribed: "Olandi",
      like: "Nalingi",
      liveChat: "Masolo ya live",
      superChat: "Super Chat",
      watchingNow: "bazali kotala sikoyo",
      share: "Kabola",
      download: "Bimisa na se",
      comments: "Bakanisi",
      writeComment: "Koma likanisi na yo...",
      comment: "Koma",
      cancel: "Tika",
      tipCreator: "Kopesa Matabisi",
      reply: "Zongisa",
      viewReplies: "Tala biyano",
      hideReplies: "Bomba biyano",
      pinnedBy: "Ekangami na",
      pinComment: "Kangama lisolo",
      unpinComment: "Longola kangama",
    },
    tipping: {
      tipCreator: "Kopesa Matabisi",
      merci: "Merci",
      modalTitle: "Pesá Lisungi",
      modalSubtitle: "Lisungi ya mbalakaka na Mobile Money na Airtel Money, Vodacom M-Pesa to Orange Money",
      currency: "Mbongo",
      presetAmounts: "Mituya mikata",
      customAmount: "Motuya mosusu",
      selectProvider: "Pona mosalisi Mobile Money",
      phoneNumber: "Nimero ya telefone Mobile Money",
      phoneNumberPlaceholder: "81 234 5678",
      message: "Likanisi ya lisungi",
      messagePlaceholder: "Komá liloba ya kopesa motema (soki olingi)...",
      sendTip: "Tinda Lisungi",
      sending: "Kosala Mobile Money...",
      tipSuccess: "Matondi mingi mpo na lisungi !",
      tipSuccessDesc: "Lisungi na yo ya Mobile Money etindami mbala moko epai ya mokeli.",
      tippedBadge: "Apesi lisungi",
      superThanks: "Matondi Mingi",
      feeBreakdown: "80% ekendaka mbala moko epai ya moyembi, 20% mpo na misala ya plateforme",
    },
    admin: {
      dashboardTitle: "Esika ya Mokambi Mobimba",
      operationsTitle: "Misala mpe bokengeli ya platfɔrmɛ",
      totalVolume: "Monene Mobimba",
      platformMargin: "Matabisi ya Platfɔrmɛ (20%)",
      creatorNet: "Mbongo ya Mosali (80%)",
      catalogHealth: "Bokolɔngɔnɔ ya Bavidéo",
      moderationTab: "Molɔngɔ́ ya bokengeli IA",
      payoutsTab: "Molɔngɔ́ ya kofuta basali",
      tableType: "Lolenge",
      tableContent: "Makambo / Kombo",
      tableAuthor: "Mokomi",
      tableReason: "Ntina ya kokebisa",
      tableScore: "Mbeba ya makambo",
      tableStatus: "Ezali ndenge nini",
      tableActions: "Misala",
      tableRequestId: "Nimero ya Bosengi",
      tableCreator: "Mosali / Chaîne",
      tableAmount: "Motuya ya Mbongo",
      tableMethod: "Lolenge ya kofuta",
      tablePhone: "Nimero ya Telefone",
      approve: "Ndima",
      reject: "Longola / Boya",
      processPayout: "Futa mbongo",
      pageNotAvailable: "Lokasa oyo ezali te",
      pageNotFoundDesc: "Nzela olandi ekoki kozala ebebi, to lokasa elongolami to ezali te.",
      commandCenter: "Esika ya boyangeli",
      switchboard: "Etanda ya misala",
      systemLogs: "Mikanda ya misala",
      concurrentViewers: "Bato bazali kotala sikoyo",
      activeBroadcasts: "Botalisi ya live",
      bandwidthEgress: "Bopanzani ya bande (24h)",
      edgeCacheHitRate: "Bokokani ya Edge Cache",
      momoGmv30d: "Mbongo ya Mobile Money (30j)",
      pendingModeration: "Kotalela ezali kozela",
      pendingDisputes: "Ntembe ezali kozela",
      pendingPayouts: "Bofuti ezali kozela",
      regionalConcentration: "Bopanzi ya bato na ba engumba",
      emergencyMaintenance: "Kokanga sistɛmɛ mpo na kobongisa",
      emergencyMaintenanceDesc: "Ekangi mbala moko esika mobimba mpo na kobongisa makambo.",
      registrationLock: "Kukanga bokomi ya sika",
      registrationLockDesc: "Etelemi kofungola ba chaînes ya sika mpo na mwa tango.",
      payoutEngine: "Mombongo ya Mobile Money",
      payoutEngineDesc: "Episaka nzela na bofuti ya mbala moko na ba comptes MoMo.",
      highBitrateCap: "Kokatisa molongo ya video (720p)",
      highBitrateCapDesc: "Epekisi video koleka 720p tango internet ezali kolɛmba na mboka.",
      confirmToggleTitle: "Ndima mbongwana ya sistɛmɛ",
      confirmToggleDesc: "Esalemi mbala moko na ba serveurs nyonso pe ekomami na mokanda ya audit.",
      confirmAction: "Ndima & Sala",
      cancelAction: "Tika",
      auditTrail: "Molongo ya makambo masalemi",
      actionFilter: "Pona ndenge ya likambo",
      entityFilter: "Pona esika",
      appliedBy: "Esalemi na",
      timestamp: "Tango",
      statusHealthy: "Ezali kosala malamu",
    },
    upload: {
      pageTitle: "Esika ya Kotinda Video ya Mosali",
      pageSubtitle: "Kotinda video mbala moko na Cloudflare Stream na lisalisi ya TUS",
      directUploadNotice: "Mbango mpe bokebi: Video na yo ezali kokende mbala moko na ba ordinatɛrɛ ya Cloudflare kozanga kolekela na srvɛrɛ ya Render.",
      dragDropText: "Benda mpe tika video na yo awa",
      orBrowse: "to finá awa mpo na kopona video na masini na yo",
      supportedFormats: "Ezali koyamba MP4, MOV, MKV, WebM kino 30GB",
      selectedFile: "Video eponami",
      fileSize: "Monene ya video",
      titleLabel: "Kombo ya Video",
      titlePlaceholder: "Komá kombo ya kitoko mpo na video...",
      descLabel: "Ndimbola ya Video",
      descPlaceholder: "Pesá makambo etali video to ba liens...",
      categoryLabel: "Lolenge ya Video",
      languageLabel: "Monoko ya Video",
      creatorProfileIdLabel: "Nimero ya Mosali Video (ID)",
      creatorProfileIdPlaceholder: "ndakisa: clxyz123abc to nimero na yo",
      maxDurationLabel: "Malamu ya ntango (Segonde)",
      startUploadButton: "Bandá kotinda video na Cloudflare Stream",
      uploadProgress: "Kokende ya video",
      uploadSpeed: "Vitesse",
      eta: "Ntango etikali",
      uploadedBytes: "Esi etindami",
      tusResumableTag: "Sistɛmɛ ya TUS: Soki internet ekati, okoki kokoba kaka wana!",
      sessionCreated: "Nzelá efungwami malamu na Cloudflare Stream",
      uploadSuccessTitle: "Video etindami malamu mpenza!",
      uploadSuccessDesc: "Video na yo ekomi na Cloudflare Stream mpe babandi kobongisa yango na ba qualite nionso.",
      streamIdLabel: "Nimero ya Media na Cloudflare (UID)",
      hlsUrlLabel: "Adresi ya HLS Manifest",
      watchPreviewButton: "Tala video na Molakisi",
      uploadAnotherButton: "Tinda video mosusu",
      errors: {
        noFile: "Pona video liboso ya kotinda.",
        noTitle: "Sengeli okoma kombo ya video.",
        noCreatorId: "Nimero ya mosali video (ID) esengeli.",
        sessionFailed: "Ekoki kokanga nzela te na Cloudflare Stream.",
        uploadFailed: "Kotinda video ebebi, meka lisusu.",
      },
    },
    categories: {
      music: "Miziki & Masano",
      education: "Mateya & Boyekoli",
      gaming: "Masano ya video",
      entertainment: "Kosekisa & Bisengo",
      cultureNews: "Mimeseno & Masolo",
      technology: "Teknolozi & Kode",
      lifestyle: "Bomoyi & Vlogs",
    },
    player: {
      pageTitle: "Molakisi Video ya HLS",
      pageSubtitle: "Kotala video na qualite ekokani na vitesse ya internet na yo mpe kokanga mpo na kotala ofline",
      enterHlsOrUid: "Luka bavidéo, bayembi, ba podcasts...",
      loadVideo: "Fungola Video",
      quality: "Bolamu ya elilingi",
      speed: "Vitesse ya kotala",
      normal: "Normal",
      sourceLocked: "Source eza ya kokanga",
      downloadOffline: "Kanga MP4 mpo na kotala na nse ya Internet (Offline)",
      downloadMp4Notice: "Kokanga mbala moko video MP4 ya solo na Cloudflare Stream",
      downloading: "Ezali kobongisa kokanga...",
      currentResolution: "Monene ya bilili sikoyo",
      bitrate: "Vitesse ya ba données (Bitrate)",
      liveStats: "Makambo ya tekiniki ya video",
      showStats: "Lakisa makambo ya tekiniki",
      hideStats: "Bomba makambo ya tekiniki",
      pip: "Bilili na mwa efelo",
      fullscreen: "Efelo mobimba",
      mute: "Kanga mongongo",
      unmute: "Fungola mongongo",
      play: "Béta",
      pause: "Tɛlɛmisa",
      buffering: "Ezali kozela internet...",
      streamMediaId: "Nimero ya Stream (UID)",
      demoVideoTitle: "Video ya ndakisa ya Helix HLS",
      demoVideoCreator: "Masini ya HP - Helix",
      chapters: "Biteni ya Video",
      chapter: "Eteni ya Video",
      autoChapters: "Fungola biteni ya video ya mbala moko",
      endScreen: "Bilili ya suka ya video",
      subscribe: "Mibatela",
      subscribed: "Omibateli",
      watchNext: "Tala oyo elandi",
      audioTrack: "Mongongo ya video",
      originalAudio: "Mongongo ya ebandeli",
      dubbedAudio: "Mongongo babongola",
      audioTrackDesc: "Bongola mongongo na minoko ndenge na ndenge",
      addAudioDub: "Bakisa mongongo mosusu",
    },
    arch: {
      title: "Lolenge ya Sistɛmɛ ya Cloudflare Stream mpe Render",
      subtitle: "Teknolozi ya makasi mpo srvɛrɛ ebeba te mpe video etambola na mbango monene.",
      zeroBackendRuleTitle: "Mobeko ya liboso: Video elekelaka na srvɛrɛ te",
      zeroBackendRuleDesc: "Video etindamaka mbala moko longwa na telefone to ordinatɛrɛ na yo kino na Cloudflare Stream, Render esalaka kaka makambo ya mikanda mpe mbongo.",
      pipelineSteps: {
        step1Title: "1. Kofungola nzela ya kokota",
        step1Desc: "Fastify API ezali kosenga nzela na Cloudflare Stream mpe ezali kobomba kombo ya video na base de données.",
        step2Title: "2. Kotinda biteni ya video na TUS",
        step2Desc: "Video ezali kokende biteni na biteni na Cloudflare; soki internet ekati, okoki kokoba esika otikaki.",
        step3Title: "3. Kobongisa ba qualite nionso",
        step3Desc: "Cloudflare ezali kobongisa video na 1080p, 720p, 480p, 360p, 240p mpe 144p.",
        step4Title: "4. Kotalisa mokili mobimba",
        step4Desc: "Cloudflare eyebisi Render ete video esili, mpe bato bakoki kotala yango to kokanga yango na MP4.",
      },
    },
    notifications: {
      title: "Biyebisi",
      all: "Nionso",
      personalized: "Ya kopona",
      none: "Moko te",
      markAllAsRead: "Tia nionso lokola etangami",
      empty: "Ezaleli ya sika te",
      newUpload: "Video ya sika etindami",
      tierUpdated: "Lolenge ya biyebisi ebongwani",
    },
    community: {
      community: "Lisanga",
      postsAndPolls: "Biyebisi & Maponi",
      createPost: "Koma liyebisi",
      postPlaceholder: "Kabola sango na lisanga na yo to tuna batali motuna...",
      addPoll: "Bakisa liponi",
      removePoll: "Longola liponi",
      pollOptions: "Maponi (2 kino 5)",
      option: "Liponi",
      addOption: "Bakisa liponi mosusu",
      public: "Mpo na banso",
      membersOnly: "Kaka mpo na basangani",
      publish: "Tinda",
      publishing: "Ezali kotinda...",
      postPublished: "Liyebisi etindami malamu !",
      contentRequired: "Koma mwa maloba mpo na liyebisi na yo.",
      pollMinOptions: "Liponi esengeli ezala na maponi mibale to koleka.",
      publishedPosts: "Biyebisi bitindami",
      noPosts: "Liyebisi moko te naino",
      noPostsDesc: "Tango mosali video akotinda biyebisi to maponi, ekobima awa.",
      deletePost: "Longola liyebisi",
      likes: "bolingi",
      vote: "Pona",
      votesCount: "maponi",
      selectToVote: "Pona liponi moko mpo na kotelema",
      clickToChangeVote: "Finika liponi mosusu mpo na kobongola",
      loadingPosts: "Ezali kozwa biyebisi...",
    },
    verification: {
      title: "Elembo ya Lisanga & Bondimi",
      badgeVerified: "Mosali Bandimi",
      badgeOfficialArtist: "Moyembi ya Solo",
      badgePartner: "Moninga ya Motuya",
      eligibilityTitle: "Kipesi Nzela ya Bondimi",
      applyButton: "Senga Bondimi",
      statusPending: "Basali bazali kotala",
      statusApproved: "Bandimi",
      statusRejected: "Baboyi kosenga",
    },
    pwa: {
      installApp: "Tia Programu ya HP-Helix",
      installDesc: "Sepela kotala video noki mpenza, kobomba video mpo na kotala na sima, na kosalela yango malamu na telefone na yo.",
      installButton: "Tia Programu",
      dismiss: "Na sima",
      pushTitle: "Biyebisi ya Telefone (Push)",
      pushDesc: "Zwa biyebisi mbala moko soki basali video oyo olandi batindi video ya sika to babandi kotalisa.",
      enablePush: "Fungola Biyebisi",
      disablePush: "Kanga Biyebisi",
      sendTestPush: "Tinda Liyebisi ya Momekano",
      testPushSuccess: "Liyebisi ya momekano etindami na browser na yo!",
      permissionBlocked: "Biyebisi bikangami. Fungola yango na ba paramɛtɛlɛ ya browser na yo.",
      installedBanner: "HP-Helix etiami mpe ezali pene mpo na kosalela ata internet ezali te.",
    },
    experiments: {
      testAndCompare: "Meka pe kokokanisa",
      testAndCompareDesc: "Meka elilingi ya likolo kino misato na kombo mosusu elongo na bato oyo batalaka yo. Algorithme eponaka tango ya kotala to mbala ya bofiniki.",
      startTest: "Banda Meka A/B",
      stopTest: "Tika Meka",
      pickWinner: "Pona oyo elongi",
      winningVariant: "Elenge elongi",
      leading: "Ezali liboso",
      slotA: "Elenge A (Ya liboso)",
      slotB: "Elenge B (Mosusu 2)",
      slotC: "Elenge C (Mosusu 3)",
      addSlotC: "+ Bakisa elilingi ya 3 (Soki olingi)",
      removeSlotC: "Longola Elenge C",
      metricLabel: "Malamu ya bomeki",
      metricWatchTime: "Kipesi ya tango ya kotala (Malamu mpo na algorithme)",
      metricCtr: "Motango ya ba finikeli (CTR)",
      impressions: "Komonana",
      clicks: "Bofiniki",
      ctrRate: "CTR",
      watchTimeShare: "Kipesi ya tango",
      runningTest: "Meka ezali kosalema",
      completedTest: "Meka esili",
      stoppedTest: "Meka etelemi",
      alternateTitle: "Kombo mosusu (Soki olingi)",
      noActiveTest: "Meka moko te ezali kosalema mpo na video oyo.",
      winnerPromotedSuccess: "Elenge oyo elongi eponami mpo na video mobimba!",
    },
    memberships: {
      join: "Kota",
      memberships: "Kokoma mosangani",
      membersOnly: "Kaka mpo na basangani",
      membershipTiers: "Ba nivo ya basangani",
      joinChannel: "Kota na chaîne",
      joinDescription: "Zwá matomba ya sipesiyale, bilembo ya bosembo pe lisungi ya solo.",
      monthlyPerks: "Matomba ya sanza na sanza",
      loyaltyBadge: "Elembo ya bosembo",
      chooseTier: "Pona nivo na yo",
      momoPayment: "Pona opérateur ya Mobile Money",
      activeMembers: "Basangani ya molende",
      monthlyRevenue: "Mbongo ya sanza (MRR)",
      createTier: "Bakisa nivo ya sika",
      tierName: "Kombo ya nivo",
      priceUsd: "Talo ya sanza ($ USD)",
      priceCdf: "Talo ya sanza (CDF)",
      perks: "Matomba",
      subscribedSince: "Mosangani banda",
      monthsMember: "ba sanza ya mosangani",
    },
    premieres: {
      premiere: "Botalisi ya liboso",
      livePremiere: "Botalisi ya sika ya solo",
      premiereStartsIn: "Botalisi ekobanda na",
      remindMe: "Bimisa ngai makanisi",
      reminderSet: "Makanisi ebombami",
      premiereCountdown: "Kanga motuya ya botalisi",
      setAsPremiere: "Bongisa botalisi ya liboso",
      waitingRoomNotice: "Esika ya kozela ezali kosala. Solola na baninga liboso video ebanda.",
      joinLiveChat: "Kota na masolo ya live",
    },
    recommendations: {
      upNext: "Elingi kolanda",
      autoplayOn: "Kofungola mbala moko",
      autoplayOff: "Kofungola mbala moko etelemi",
      cancel: "Tika",
      playNow: "Béta sikoyo",
      regionalTrending: "Ezali kotambola mingi na RDC",
      dataSaverActive: "Kobomba ba données ezali kosala",
      similarCategory: "Ndenge moko",
      fromFavoriteChannel: "Wuta na ba chaîne olingaka",
    },
    maintenance: {
      headline: "HP-Helix Video Streaming | Ekozala Noki",
      subtitle: "Platform ya video mpo na RDC mpe Afrika ezali sikoyo na komekama ya sekele.",
      badge: "Ezali Kotongama",
      devAccess: "Kokota ya ba Développeurs / Creator Early Access",
      devModalTitle: "Kokota ya ba Développeurs na Ba Créateurs",
      devModalSubtitle: "Koma liloba ya sekele mpo na kofungola platform mobimba.",
      passcodePlaceholder: "Koma liloba ya sekele (ndakisa: helix_alpha_2026)...",
      unlockButton: "Fungola Platform",
      unlocking: "Kofungola...",
      invalidPasscode: "Liloba ya sekele ezali ya solo te. Meka lisusu.",
      featureHlsTitle: "Kotala Video ya Noki 4K HLS",
      featureHlsDesc: "Bokotisi video mbala moko na Cloudflare Stream CDN kozanga kotiya kilo na serveur.",
      featureMobileTitle: "Kobomba ba Données ya Téléphone",
      featureMobileDesc: "Ebongisami mpo na basali ya RDC mpe Afrika ya Este na kobomba video offline.",
      featureMonetizationTitle: "Kofuta Ba Créateurs Mbala Moko",
      featureMonetizationDesc: "Kofuta mbongo mbala moko na M-Pesa, Airtel Money mpe Orange Money.",
      featureChatTitle: "Botalisi ya Liboso na Masolo ya Live",
      featureChatDesc: "Ntango ya kolanda elongo na masolo ya live mbala moko.",
    },
    clips: {
      clip: "Eteni",
      createClip: "Kosalela eteni",
      watchFullVideo: "Tala video mobimba",
      shareClip: "Kabola eteni",
      clipDuration: "Mola ya eteni",
      titlePlaceholder: "Bakisá motó ya likambo ya eteni...",
      titleRequired: "Motó ya likambo esengeli (100 caractères max)",
      characterCount: "maloba",
      previewLoop: "Kotala na kozonga sima",
      copiedLink: "Lien ya eteni ekangami na presse-papiers!",
      copyLink: "Kopya lien",
      shareWhatsApp: "Kabola na WhatsApp",
      shareX: "Kabola na X",
      shareFacebook: "Kabola na Facebook",
      minDurationError: "Eteni esengeli kozala na mwa ba secondes 5.",
      maxDurationError: "Eteni ekoki koleka ba secondes 60 te.",
      creating: "Kosala eteni...",
      viewClip: "Tala eteni",
      viewsCount: "bataleli",
      startTime: "Ebandeli",
      endTime: "Suka",
    },
    studioUpload: {
      uploadVideo: "Kotia video",
      selectFromVideoFrames: "Pona na bifilme",
      uploading: "Ezali kokota...",
      standardVideoBadge: "Video ya Monene (16:9)",
      shortBadge: "Video ya Mokuse (9:16)",
      customThumbnail: "Elili ya ponami",
      uploadCustomThumbnail: "Kotia elili ya sika",
      uploadSpeed: "Vitesse ya bokotisi",
      timeRemaining: "Ntango etikali",
      pauseUpload: "Tɛlɛmisa mwa moke",
      resumeUpload: "Koba lisusu",
      cancelUpload: "Tika",
      visibilityLabel: "Bomonisi",
      monetizationOptIn: "Fungola kofuta na Mobile Money",
      mobileMoneyPayouts: "Kozwa mbongo na Airtel Money / M-Pesa / Orange Money",
      titleAutoPopulated: "Kombo eponami mbala moko na dosiye",
      probingMedia: "Koluka lolenge ya video & kokanga bilili...",
    },
    merch: {
      store: "Magazini",
      merchandise: "Biloko ya solo",
      officialMerch: "Biloko ya solo ya créateur",
      buyNow: "Somba sikoyo",
      order: "Kanga biloko",
      inStock: "Ezali na stock",
      outOfStock: "Esili na stock",
      deliveryCity: "Engumba ya bokaboli",
      deliveryAddress: "Adresi ya ndako",
      buyerName: "Kombo mobimba",
      buyerPhone: "Numéro ya Mobile Money (M-Pesa / Airtel / Orange)",
      paymentMethod: "Ndengé ya kofuta",
      momoPaymentPrompt: "Prompt ya kofuta na Mobile Money ekotinda na téléphone na yo.",
      placeOrder: "Kofuta sikoyo na Mobile Money",
      placingOrder: "Kotinda prompt...",
      orderSuccess: "Kosomba esalemi malamu!",
      orderSuccessDesc: "Kota na téléphone na yo mpe kotia code secret ya Mobile Money mpo na kofuta.",
      pinnedMerch: "Biloko ya motuya ya créateur",
      allProducts: "Biloko nyonso",
      apparel: "Bilamba",
      musicVinyl: "Miziki & Vinyles",
      accessories: "Biloko ya motuya",
      close: "Kanga",
    },
    moderation: {
      heldForReview: "Ezali kozela kotalelama",
      published: "Epanzani",
      reportContent: "Funda eloko oyo",
      reportSuccess: "Matondi mpo na kofunda. Tokotalela yango noki penza.",
      approve: "Kondima",
      remove: "Kolongola",
      blockAuthor: "Kopekisa mokomi",
      approveAll: "Kondima nionso",
      removeAll: "Kolongola nionso",
      commentsQueue: "Botaleli ya maloba ezali kozela",
      noHeldComments: "Maloba moko te ezali kozela kotalelama.",
      noPublishedComments: "Maloba moko te epanzani naino.",
      selectAll: "Pona nionso",
      selectedCount: "biponami",
      reportReason: "Pona ntina ya kofunda",
      reasonSpam: "Nzela mabe to spam",
      reasonHateSpeech: "Kozinda to kofinga",
      reasonCopyright: "Koyiba loyembo to video",
      reasonExplicit: "Eloko ya soni to ya mobulu",
      additionalDetails: "Makambo mosusu (soki olingi)",
      detailsPlaceholder: "Yebisa biso makambo mosusu...",
      submitReport: "Tinda kofunda",
      cancel: "Tika",
      authorBlockedNotice: "Mokomi apekisami mpo na chaîne oyo.",
      commentApprovedNotice: "Maloba endimami mpe epanzani.",
      commentRemovedNotice: "Maloba elongolami.",
    },
    live: {
      live: "NA DIRECT",
      liveBadge: "NA DIRECT",
      rewindToLive: "Zonga na direct",
      streamEnded: "Direct esili",
      enableDvr: "Kofungola bozongi DVR (Kozonga kino ngonga 2)",
      enableDvrDesc: "Epesi bato nzela ya kotelema mpe kozonga sima na kati ya direct.",
      autoPublishVod: "Kobimisa mbala moko lokola video ya seko (VOD)",
      autoPublishVodDesc: "Ekokomisa direct oyo video ya seko soki esili.",
      liveReplay: "Bozongi ya direct",
      goToLive: "Zonga na direct",
      liveStream: "Bopanzi ya direct",
    },
    playlists: {
      playlists: "Molongo ya bafilme",
      playlist: "Molongo ya bafilme",
      saveToPlaylist: "Bomba na molongo",
      createPlaylist: "Kela molongo ya sika",
      playlistTitle: "Kombo ya molongo",
      privacy: "Kobombama",
      public: "Mpo na bato nionso",
      unlisted: "Kaka mpo na baoyo baza na link",
      private: "Kaka mpo na yo",
      shuffle: "Kopona na mbalakaka",
      loop: "Kozongela molongo",
      loopOn: "Kozongela molongo : EFUNGOLAMI",
      loopOff: "Kozongela molongo : EKANGAMI",
      mix: "Mix ya sika",
      mixDesc: "Mix ya kokoba kolanda video oyo",
      videoCount: "bavideo",
      emptyPlaylist: "Molongo moko te ezwami. Kela ya sika na nse !",
    },
    panoramic: {
      panoramicVideo: "Video ya nzinganzinga",
      vrMode: "Botalisi ya VR",
      cardboardMode: "Botalisi ya VR Cardboard",
      dragToLook: "Gumba mpo na kotala",
      tiltDevice: "Gumbisa telefone mpo na kotala",
      resetBearing: "Zongisa na Nord",
      compass: "Busole",
      projectionEquirectangular: "Nzinganzinga ya mobimba (360° Sphere)",
      projectionFlat: "Botalisi ya patata",
      recordedIn360: "Video oyo ekangamaki na lolenge ya 360° / VR",
      recordedIn360Desc: "Fungola botalisi ya 360° nzinganzinga mpe kotambola na video oyo.",
    },
    offlineVault: {
      download: "Bimisa na se",
      downloading: "Kobimisa ezali kokoba...",
      downloaded: "Ebimisami",
      offlineLibrary: "Bibiyotɛkɛ ya nse ya internet",
      storage: "Esika ya kobomba",
      storageUsed: "Esika esalelami",
      clearAll: "Longola nionso",
      deleteFromDevice: "Longola na telefone",
      dataSaver: "Kobomba megas (360p)",
      standardQuality: "Lolenge ya momeseno (480p)",
      highDefinition: "Lolenge ya likolo (720p)",
      offlineModeActive: "Lolenge ya kozanga internet",
      offlineModeNotice: "Lolenge ya kozanga internet ezali kosala - Kolakisa uta na bibiyotɛkɛ na yo",
      noVideosDownloaded: "Video moko te ebimisami naino",
      noVideosDescription: "Ntango ozali kotala video, finá buto ya Kobimisa mpo na kobomba yango mpe kotala kozanga internet.",
      exploreVideos: "Tala bavideo ya kobimisa",
      startDownload: "Banda kobimisa",
      chooseResolution: "Pona lolenge ya video",
      fullOfflinePlayback: "Kotala kozanga internet ezali kosala na Service Worker mpe OPFS.",
      alreadySaved: "Ebombami kala na bibiyotɛkɛ na yo",
      readyForPlayback: "Ezali pene mpo na kotala kozanga unite ya internet.",
      removeFromDevice: "Longola na telefone",
      close: "Kanga",
      cancel: "Tika",
      confirmClearAll: "Olingi solo kolongola bavideo nionso ebombami na telefone ?",
      confirmDelete: "Olingi kolongola video oyo na telefone ?",
      resolution360pDesc: "Lolenge ya nse (360p) - Kobomba megas (~15 MB)",
      resolution480pDesc: "Lolenge ya katikati (480p) - Ya momeseno (~35 MB)",
      resolution720pDesc: "Lolenge ya likolo (720p) - HD (~75 MB)",
    },
    payouts: {
      revenueAndPayouts: "Mbongo & Kobimisa",
      requestPayout: "Senga kobimisa mbongo",
      availableBalance: "Mbongo oyo ezali",
      settlementComplete: "Mbongo etindami",
      lifetimeEarnings: "Mbongo nionso ezwama",
      minimumThresholdNotice: "Kobimisa ya nse : 5 $ USD to 12 500 CDF",
      configureWallet: "Bongisa porte-monnaie",
      walletDetails: "Porte-monnaie Mobile Money",
      phoneNumber: "Nimero ya telefone Mobile Money",
      accountName: "Kombo ya nkolo konte",
      provider: "Mopanzi ya mbongo",
      monthlyRunRate: "Kotalela ya sanza",
      platformFee: "Lopango ya plateforme (20%)",
      netEarnings: "Mbongo ya mosali video (80%)",
      pendingPayouts: "Mbongo ezali koya",
      withdrawAmount: "Motango ya kobimisa",
      history: "Lisolo ya kobimisa mbongo",
      statusPaid: "Efutami",
      statusProcessing: "Ezali kotinda",
      statusFailed: "Elongi te",
      statusPending: "Ezali kozela",
      disbursementQueued: "Losambo ya kobimisa etindami malamu",
      saveWallet: "Bomba porte-monnaie",
      currency: "Mbongo",
      allRetire: "Bimisa nionso (100%)",
    },
    coStreaming: {
      coStreaming: "Botalisi na baninga",
      goLiveTogether: "Botalisi na baninga",
      inviteGuest: "Benga moninga",
      inviteGuestDesc: "Benga moninga mosali video to mopanzi sango mpo na koloba elongo na botalisi ya live.",
      copyInviteLink: "Kopya lien ya libenga",
      linkCopied: "Lien ekopisi malamu!",
      waitingInGreenroom: "Kozela kokota",
      guestConnected: "Moninga akoti",
      sideBySide: "Bipai mibale (50/50)",
      pictureInPicture: "Elili na kati ya elili",
      enterStream: "Kota na botalisi",
      connectingToHost: "Ezali kosangana na botalisi...",
      readyToJoin: "Omibongisi kokota",
      cameraPreview: "Kotala kamera",
      micTest: "Komeka micro",
      audioLevel: "Mongongo",
      muteGuestAudio: "Kanga mongongo",
      unmuteGuestAudio: "Fungola mongongo",
      muteGuestVideo: "Kanga kamera",
      unmuteGuestVideo: "Fungola kamera",
      removeGuest: "Bimisa moninga",
      leaveStream: "Bima na botalisi",
      guestDisconnected: "Moninga abimi na botalisi",
      hostBroadcast: "Botalisi ya nkolo",
      liveSplitView: "Botalisi ya bato mibale",
      guestNameLabel: "Kombo na yo",
      guestNamePlaceholder: "Koma kombo na yo...",
      onAirBadge: "LIVE",
      activeGuests: "Baninga bazali na botalisi",
      noGuestsJoined: "Moninga moko te akoti naino. Tinda lien oyo ezali likolo.",
    },
    copyright: {
      copyrightMatchTool: "Esika ya bokengi bwa mikanda ya bayemba",
      matchesAndClaims: "Bikokani & Bosengi",
      registeredAssets: "Miziki pe Biyembo Bibombami",
      receivedClaims: "Bosengi Boyambami",
      registerMasterStem: "Koma Nzembo ya Moko / Master Stem",
      contentIdWarning: "Kébisa mpo na lotómo ya mokomi (Content ID)",
      submitDispute: "Kozongela bosengi",
      disputeClaim: "Wana ezali nzembo na ngai",
      requestTakedown: "Senga bolongoli",
      monetize: "Zwa Mbongo",
      track: "Landa ba stats",
      block: "Kanga",
      disputeReason: "Ntina ya kozongela",
      disputeExplanation: "Ndimbola ya mozindo",
      disputeSubmitted: "Bosengi bozongisami na molende! Balandi bazali kotala.",
      disputeFairUse: "Bosaleli ya ndingisa (Fair Use)",
      disputeOriginalCreator: "Nzembo ya ngai moko 100%",
      disputeLicensed: "Nazwi ndingisa ya mokomi",
      audioFingerprintMatch: "Elembo ya lokito ya nzembo ekokani",
      confidence: "Bokokani",
      matchedSegment: "Eteni ya nzembo",
      releaseClaim: "Longola bosengi",
      claimReleased: "Bosengi bolongolami.",
      noMatchesFound: "Nzembo na yo moko te eyebani ete moto mosusu azwi.",
      noAssetsFound: "Nzembo moko te ekomami naino. Batela miziki na yo.",
      noReceivedClaims: "Bosengi moko te ezali na ba video na yo.",
      assetTitle: "Kombo ya nzembo",
      artist: "Moyembi / Mokomi",
      isrc: "Nimero ISRC",
      duration: "Tango (ba segonde)",
      defaultPolicy: "Mibeko ya mobu",
      registerAssetSuccess: "Nzembo ebombami pe elembo ya Content ID esalemi!",
      filterAll: "Nyonso",
      filterActive: "Oyo ezali kosala",
      filterDisputed: "Ezali na ntembe",
      filterResolved: "Esili / Elongolami",
      statusActive: "Ezali kosala",
      statusDisputed: "Na ntembe",
      statusReleased: "Elongolami",
      statusTakedownRequested: "Basengi bolongoli",
    },
  },
  sw: {
    common: {
      appName: "HP - Helix",
      tagline: "Mfumo wa Kisasa wa Video na Mapato ya Moja kwa Moja kwa Wabunifu",
      upload: "Studio ya Kupakia",
      player: "Kicheza Video",
      architecture: "Muundo wa Mfumo",
      ready: "Tayari",
      processing: "Inachakatwa",
      uploading: "Inapakia...",
      failed: "Imeshindwa",
      paused: "Imesimamishwa",
      cancel: "Ghairi",
      pause: "Sitisha",
      resume: "Endelea",
      save: "Hifadhi",
      loading: "Inapakia...",
      error: "Hitilafu",
      success: "Mafanikio",
      auto: "Kiotomatiki",
    },
    nav: {
      home: "Nyumbani",
      shorts: "Video Fupi",
      trending: "Zinazovuma",
      subscriptions: "Usajili Wangu",
      history: "Historia",
      offlineLibrary: "Maktaba ya Nje ya Mtandao",
      uploadStudio: "Studio ya Kupakia",
      studio: "Studio ya HP-Helix",
      adminPortal: "Tovuti ya Msimamizi",
      architecture: "Muundo wa Mfumo",
      settings: "Mipangilio",
      all: "Zote",
      music: "Muziki",
      podcasts: "Vipindi (Podcasts)",
      tech: "Teknolojia",
      entertainment: "Burudani",
      cinema: "Sinema",
      africanCinema: "Sinema",
      gaming: "Michezo",
      culture: "Utamaduni na Habari",
      viewChannel: "Tazama kituo chako",
      uploadVideo: "Pakia video",
      signOut: "Ondoka",
    },
    home: {
      recommendedVideos: "Video Zinazopendekezwa",
      videosAvailable: "Video {{count}} zinapatikana",
      views: "{{count}} mara zilizotazamwa",
      view: "{{count}} mara iliyotazamwa",
      hoursAgo: "saa {{count}} zilizopita",
      daysAgo: "siku {{count}} zilizopita",
      weeksAgo: "wiki {{count}} zilizopita",
      justNow: "Sasa hivi",
      recently: "Hivi karibuni",
    },
    studio: {
      uploadVideos: "Pakia video",
      dragDropTitle: "Buruta na udondoshe faili za video ili kupakia",
      dragDropSubtitle: "Video zako zitakuwa za siri hadi utakapozichapisha. Inasaidia MP4, WebM, MOV, na ProRes ya ubora wa juu.",
      videoFormat: "Muundo na Uzoefu wa Video",
      standardVideo: "Video ya Kawaida (16:9)",
      shortReel: "Video Fupi / Reel (9:16)",
      relatedVideo: "Video Husika",
      relatedVideoDesc: "Unganisha Video Fupi hii moja kwa moja na video yake ndefu asili. Watazamaji wataona kitufe cha kuelekea moja kwa moja kwenye video kamili.",
      selectLongVideo: "Chagua Video Ndefu",
      noneStandalone: "-- Hakuna / Video Fupi ya Kujitegemea --",
      jumpTimestamp: "Rukia muda katika video ndefu (sekunde au mm:ss)",
      audience: "Watazamaji",
      audienceQuestion: "Je, video hii imeundwa kwa ajili ya watoto? (inahitajika)",
      coppaNotice: "Bila kujali eneo lako, unalazimika kisheria kutii Sheria ya Kulinda Faragha ya Watoto Mtandaoni (COPPA).",
      madeForKidsYes: "Ndiyo, imeundwa kwa ajili ya watoto",
      madeForKidsNo: "Hapana, haijaundwa kwa ajili ya watoto",
      next: "Inayofuata",
      back: "Rudi Nyuma",
      publishVideo: "Chapisha Video",
      publishing: "Inachapisha...",
      uploadCompleteChecksPassed: "Upakiaji umekamilika. Ukaguzi umefaulu.",
      channelDashboard: "Dashibodi ya Kituo",
      welcomeCreator: "Karibu kwenye kitovu chako cha ubunifu, @{{handle}}",
      latestVideoPerformance: "Ufanisi wa video ya hivi punde",
      channelAnalytics: "Takwimu za kituo",
      currentSubscribers: "Wanaofuatilia sasa",
      summaryLast28Days: "Muhtasari (Siku 28 zilizopita)",
      views: "Mionekano",
      watchTimeHours: "Muda wa kutazama (saa)",
      estimatedFanSupport: "Msaada unaokadiriwa wa mashabiki",
      creatorInsightsNews: "Taarifa na vidokezo vya wabunifu",
      uploadVideo: "Pakia video",
      askStudio: "Msaidizi wa Studio",
      switchToApp: "Rudi kwenye Programu",
      edgeTitle: "Usambazaji wa Kasi ya Juu Umeanza",
      edgeDescription: "HP-Helix Edge Delivery is serving optimized, low-data streams across Kinshasa, Goma, Lubumbashi, Nairobi, and regional hubs.",
      audienceRetention: "Ubakiaji wa watazamaji",
      averageViewDuration: "Wastani wa muda wa kutazama",
      averagePercentageViewed: "Wastani wa asilimia iliyotazamwa",
      retentionSubtitle: "Mkondo wa ubakiaji katika muda wote wa video",
      selectVideo: "Chagua video",
      introBenchmark: "Kipimo cha utangulizi (00:30)",
      introDropoff: "Ubakiaji wa utangulizi (00:30)",
      introGreat: "Utangulizi unavutia vizuri",
      introTypical: "Upungufu wa kawaida",
      introNote: "{pct}% ya watazamaji bado wanatazama kwenye 00:30. Utangulizi imara hupunguza watazamaji wanaoondoka.",
      spikesTitle: "Miinuko na marudio",
      spikesNote: "Miinuko inaonyesha sehemu ambazo watazamaji walizitazama tena au walizishiriki na marafiki.",
      rewatch: "Imetazamwa tena",
      ofTotal: "kati ya {total} jumla",
      retentionNoData: "Bado hakuna data ya kutosha ya utazamaji",
      retentionNoDataHint: "Ubakiaji utaonekana hapa watazamaji watakapotazama video hii.",
      retentionLoading: "Inapakia data ya ubakiaji…",
      retentionError: "Imeshindwa kupakia data ya ubakiaji.",
      retentionForbidden: "Huna ruhusa ya kuona takwimu za video hii.",
      sidebar: {
        dashboard: "Dashibodi",
        content: "Maudhui",
        playlists: "Orodha za kucheza",
        analytics: "Takwimu",
        community: "Jumuiya",
        subtitles: "Tafsiri ndogo (Subtitles)",
        contentDetection: "Ugunduzi wa maudhui",
        earn: "Mapato",
        customization: "Ubinafsishaji",
        audioLibrary: "Maktaba ya sauti",
        settings: "Mipangilio",
        sendFeedback: "Tuma maoni",
      },
      goLive: "Rusha moja kwa moja",
    },
    watch: {
      createdFrom: "Imetengenezwa kutoka:",
      subscribe: "Jiunge",
      subscribed: "Umejiunga",
      like: "Penda",
      liveChat: "Gumzo la moja kwa moja",
      superChat: "Super Chat",
      watchingNow: "wanatazama sasa",
      share: "Shiriki",
      download: "Pakua",
      comments: "Maoni",
      writeComment: "Andika maoni...",
      comment: "Toa Maoni",
      cancel: "Ghairi",
      tipCreator: "Toa Shukrani",
      reply: "Jibu",
      viewReplies: "Tazama majibu",
      hideReplies: "Ficha majibu",
      pinnedBy: "Imebandikwa na",
      pinComment: "Bandomisha maoni",
      unpinComment: "Ondoa ubandiko",
    },
    tipping: {
      tipCreator: "Toa Shukrani",
      merci: "Asante",
      modalTitle: "Mpe Zawadi Muundaji",
      modalSubtitle: "Usaidizi wa moja kwa moja kupitia Airtel Money, Vodacom M-Pesa, au Orange Money",
      currency: "Sarafu",
      presetAmounts: "Kiasi Kilichopendekezwa",
      customAmount: "Kiasi Kingine",
      selectProvider: "Chagua Mtoa Huduma wa Mobile Money",
      phoneNumber: "Nambari ya Simu ya Mobile Money",
      phoneNumberPlaceholder: "81 234 5678",
      message: "Ujumbe wa Shukrani",
      messagePlaceholder: "Andika ujumbe wa pongezi (si lazima)...",
      sendTip: "Tuma Zawadi",
      sending: "Inachakata Mobile Money...",
      tipSuccess: "Asante sana kwa msaada wako!",
      tipSuccessDesc: "Zawadi yako imetumwa moja kwa moja kwa muundaji.",
      tippedBadge: "Ametuma zawadi",
      superThanks: "Shukrani Kubwa",
      feeBreakdown: "80% huenda moja kwa moja kwa mtengenezaji, 20% ada ya miundombinu ya jukwaa",
    },
    admin: {
      dashboardTitle: "Dashibodi ya Usimamizi wa Jumla",
      operationsTitle: "Uendeshaji wa Mfumo na Ukaguzi",
      totalVolume: "Jumla ya Miamala",
      platformMargin: "Faida ya Mfumo (20%)",
      creatorNet: "Mabaki ya Mbunifu (80%)",
      catalogHealth: "Afya ya Katalogi",
      moderationTab: "Foleni ya Ukaguzi wa AI",
      payoutsTab: "Foleni ya Malipo ya Wabunifu",
      tableType: "Aina",
      tableContent: "Maudhui / Kichwa",
      tableAuthor: "Mwandishi",
      tableReason: "Sababu ya Kuripoti",
      tableScore: "Kiwango cha Madhara",
      tableStatus: "Hali",
      tableActions: "Vitendo",
      tableRequestId: "Kitambulisho cha Ombi",
      tableCreator: "Mbunifu / Kituo",
      tableAmount: "Kiasi",
      tableMethod: "Njia ya Malipo",
      tablePhone: "Nambari ya Simu",
      approve: "Idhinisha",
      reject: "Ondoa / Kataa",
      processPayout: "Kamilisha Malipo",
      pageNotAvailable: "Ukurasa huu haupatikani",
      pageNotFoundDesc: "Kiungo ulichofuata huenda kimevunjika, au ukurasa umeondolewa au haupo.",
      commandCenter: "Kituo cha amri",
      switchboard: "Bodi ya mfumo",
      systemLogs: "Kumbukumbu za mfumo",
      concurrentViewers: "Watazamaji wa wakati mmoja",
      activeBroadcasts: "Matangazo ya moja kwa moja",
      bandwidthEgress: "Bandwidth iliyotumika (24h)",
      edgeCacheHitRate: "Kiwango cha Edge Cache",
      momoGmv30d: "Jumla ya Mobile Money (Siku 30)",
      pendingModeration: "Ukaguzi unaosubiri",
      pendingDisputes: "Migogoro inayosubiri",
      pendingPayouts: "Malipo yanayosubiri",
      regionalConcentration: "Mgawanyo wa watazamaji kikanda",
      emergencyMaintenance: "Hali ya dharura ya matengenezo",
      emergencyMaintenanceDesc: "Inafunga mara moja jukwaa la umma kwa ajili ya matengenezo.",
      registrationLock: "Kufunga usajili mpya",
      registrationLockDesc: "Inasimamisha kwa muda usajili wa chaneli mpya za wabunifu.",
      payoutEngine: "Mfumo wa Malipo ya Mobile Money",
      payoutEngineDesc: "Inawezesha ulipaji wa kiotomatiki kwa pochi za Mobile Money.",
      highBitrateCap: "Kikomo cha Ubora wa Juu (720p)",
      highBitrateCapDesc: "Inazuia video kuzidi 720p wakati mtandao wa kikanda unapopungua.",
      confirmToggleTitle: "Thibitisha Mabadiliko ya Bodi ya Mfumo",
      confirmToggleDesc: "Hatua hii inaanza kufanya kazi mara moja kwenye maeneo yote na inarekodiwa kwenye kumbukumbu.",
      confirmAction: "Thibitisha & Tumia",
      cancelAction: "Ghairi",
      auditTrail: "Kumbukumbu zisizobadilika za ukaguzi",
      actionFilter: "Chuja kwa kitendo",
      entityFilter: "Chuja kwa huluki",
      appliedBy: "Imetekelezwa na",
      timestamp: "Muda",
      statusHealthy: "Inafanya kazi vizuri",
    },
    upload: {
      pageTitle: "Studio ya Kupakia ya Mbunifu",
      pageSubtitle: "Pakia Moja kwa Moja kwenye Cloudflare Stream kwa Itifaki ya TUS",
      directUploadNotice: "Ulinzi wa Seva: Faili za video zinapakiwa moja kwa moja kutoka kwenye kivinjari chako hadi vituo vya kimataifa vya Cloudflare bila kupitia seva ya Render.",
      dragDropText: "Buruta na udondoshe faili ya video hapa",
      orBrowse: "au bofya kuchagua faili kutoka kwenye kifaa chako",
      supportedFormats: "Inasaidia MP4, MOV, MKV, WebM hadi GB 30",
      selectedFile: "Faili Iliyochaguliwa",
      fileSize: "Ukubwa wa Faili",
      titleLabel: "Kichwa cha Video",
      titlePlaceholder: "Weka kichwa cha kuvutia cha video...",
      descLabel: "Maelezo ya Video",
      descPlaceholder: "Toa maelezo, sifa, au viungo kwa ajili ya watazamaji...",
      categoryLabel: "Kategoria",
      languageLabel: "Lugha ya Sauti / Kuu",
      creatorProfileIdLabel: "Kitambulisho cha Wasifu wa Mbunifu (ID)",
      creatorProfileIdPlaceholder: "mfano: clxyz123abc au Kitambulisho chako cha Mbunifu",
      maxDurationLabel: "Muda wa Juu (Sekunde)",
      startUploadButton: "Anza Kupakia Moja kwa Moja kwenye Cloudflare Stream",
      uploadProgress: "Maendeleo ya Upakiaji",
      uploadSpeed: "Kasi ya Upakiaji",
      eta: "Muda Uliobaki Unaokadiriwa",
      uploadedBytes: "Iliyopakiwa",
      tusResumableTag: "Itifaki ya TUS: Upakiaji unaoweza kuendelea baada ya kukatika umewashwa",
      sessionCreated: "Kipindi kilichoidhinishwa kimeundwa kwenye Cloudflare Stream",
      uploadSuccessTitle: "Upakiaji Umekamilika kwa Mafanikio!",
      uploadSuccessDesc: "Video yako imefika kwenye Cloudflare Stream na ubadilishaji wa HLS wa viwango mbalimbali umeanza.",
      streamIdLabel: "Kitambulisho cha Video ya Stream (UID)",
      hlsUrlLabel: "Kiungo cha Manifest ya HLS",
      watchPreviewButton: "Tazama Kwenye Kicheza Video",
      uploadAnotherButton: "Pakia Video Nyingine",
      errors: {
        noFile: "Tafadhali chagua faili ya video ya kupakia.",
        noTitle: "Kichwa cha video kinahitajika.",
        noCreatorId: "Kitambulisho cha wasifu wa mbunifu kinahitajika.",
        sessionFailed: "Imeshindwa kupata kiungo cha upakiaji kutoka kwenye seva.",
        uploadFailed: "Hitilafu imetokea wakati wa kupakia kupitia TUS.",
      },
    },
    categories: {
      music: "Muziki na Maonyesho",
      education: "Elimu na Mafunzo",
      gaming: "Michezo ya Video",
      entertainment: "Burudani na Vichekesho",
      cultureNews: "Utamaduni na Makala",
      technology: "Teknolojia na Uandishi wa Kanuni",
      lifestyle: "Maisha na Vlogs",
    },
    player: {
      pageTitle: "Kicheza Video cha HLS cha Kisasa",
      pageSubtitle: "Uchezaji wa video wa viwango mbalimbali kulingana na kasi ya intaneti na upakuaji wa nje ya mtandao",
      enterHlsOrUid: "Tafuta video, wasanii, podikasti...",
      loadVideo: "Fungua Video",
      quality: "Ubora wa video",
      speed: "Kasi ya kucheza",
      normal: "Kawaida",
      sourceLocked: "Chanzo kimefungwa",
      downloadOffline: "Pakua MP4 kwa Kutazama Nje ya Mtandao (Offline)",
      downloadMp4Notice: "Upakuaji wa moja kwa moja wa faili asili ya MP4 kutoka Cloudflare Stream",
      downloading: "Inaandaa upakuaji...",
      currentResolution: "Ubora Unaotumika Sasa",
      bitrate: "Kiwango cha Data (Bitrate)",
      liveStats: "Takwimu za Moja kwa Moja",
      showStats: "Onyesha Takwimu za Mtandao",
      hideStats: "Ficha Takwimu za Mtandao",
      pip: "Picha Ndani ya Picha",
      fullscreen: "Skrini Kamili",
      mute: "Zima Sauti",
      unmute: "Washa Sauti",
      play: "Cheza",
      pause: "Sitisha",
      buffering: "Inasubiri mtandao...",
      streamMediaId: "Kitambulisho cha Stream (UID)",
      demoVideoTitle: "Video ya Majaribio ya Helix Adaptive HLS",
      demoVideoCreator: "Mtambo wa Kurusha Video wa HP - Helix",
      chapters: "Sehemu za Video",
      chapter: "Sehemu",
      autoChapters: "Wezesha sehemu za kiotomatiki kutoka kwenye maelezo",
      endScreen: "Skrini ya mwisho",
      subscribe: "Jiunge",
      subscribed: "Umejiunga",
      watchNext: "Tazama inayofuata",
      audioTrack: "Mlio wa sauti",
      originalAudio: "Sauti ya asili",
      dubbedAudio: "Sauti iliyorekodiwa",
      audioTrackDesc: "Sauti za lugha mbalimbali",
      addAudioDub: "Ongeza sauti ya lugha",
    },
    arch: {
      title: "Muundo wa Cloudflare Stream + Render",
      subtitle: "Mfumo wenye nguvu ya juu bila kulemea seva au kumbukumbu ya Render.",
      zeroBackendRuleTitle: "Kanuni Kuu: Video Haipitii Kwenye Seva ya Render",
      zeroBackendRuleDesc: "Faili mbichi za video hazipitii kwenye seva ya Render Fastify. Upakiaji wote unafanywa moja kwa moja kutoka kwenye kifaa cha mtumiaji hadi Cloudflare Stream kupitia viungo salama vya TUS.",
      pipelineSteps: {
        step1Title: "1. Kuunda Kipindi Salama",
        step1Desc: "Kivinjari kinaomba kiungo kutoka Fastify API. Seva inaomba tokeni ya TUS kutoka Cloudflare na kuhifadhi taarifa za video.",
        step2Title: "2. Upakiaji wa Vipande vya TUS",
        step2Desc: "Kivinjari kinapakia vipande moja kwa moja Cloudflare Stream; mtandao ukikatika, upakiaji unaendelea pale ulipoishia.",
        step3Title: "3. Ubadilishaji wa Viwango vya HLS",
        step3Desc: "Cloudflare Stream inabadilisha video kuwa 1080p, 720p, 480p, 360p, 240p, na 144p.",
        step4Title: "4. Upatikanaji wa Kimataifa",
        step4Desc: "Cloudflare inatuma ujumbe (webhook) kwa seva ya Render ikithibitisha video iko tayari kwa utazamaji wa kimataifa na upakuaji wa MP4.",
      },
    },
    notifications: {
      title: "Taarifa",
      all: "Zote",
      personalized: "Zilizobinafsishwa",
      none: "Hakuna",
      markAllAsRead: "Weka zote kama zimesomwa",
      empty: "Hakuna taarifa mpya",
      newUpload: "Video mpya imepakiwa",
      tierUpdated: "Mapendeleo ya taarifa yamesasishwa",
    },
    community: {
      community: "Jumuiya",
      postsAndPolls: "Machapisho na Kura",
      createPost: "Unda chapisho",
      postPlaceholder: "Shiriki sasisho na jumuiya yako au waulize mashabiki swali...",
      addPoll: "Ongeza kura ya maoni",
      removePoll: "Ondoa kura ya maoni",
      pollOptions: "Chaguo za kura (2 hadi 5)",
      option: "Chaguo",
      addOption: "Ongeza chaguo",
      public: "Hadharani",
      membersOnly: "Wanachama Pekee",
      publish: "Chapisha",
      publishing: "Inachapisha...",
      postPublished: "Chapisho limechapishwa kikamilifu!",
      contentRequired: "Tafadhali weka maudhui ya chapisho lako.",
      pollMinOptions: "Kura ya maoni inahitaji angalau chaguo 2 halali.",
      publishedPosts: "Machapisho Yaliyochapishwa",
      noPosts: "Hakuna machapisho ya jumuiya bado",
      noPostsDesc: "Mbunifu huyu akichapisha taarifa au kura za maoni, zitaonekana hapa.",
      deletePost: "Futa chapisho",
      likes: "vipendwa",
      vote: "Piga kura",
      votesCount: "kura",
      selectToVote: "Chagua chaguo ili kupiga kura",
      clickToChangeVote: "Bofya chaguo jingine kubadilisha kura yako",
      loadingPosts: "Inapakia machapisho ya jumuiya...",
    },
    verification: {
      title: "Beji za Idhaa & Uthibitisho",
      badgeVerified: "Muundaji Aliyethibitishwa",
      badgeOfficialArtist: "Msanii Rasmi",
      badgePartner: "Mshirika Rasmi",
      eligibilityTitle: "Vigezo vya Kustahiki",
      applyButton: "Omba Uthibitisho",
      statusPending: "Ombi linakaguliwa",
      statusApproved: "Imethibitishwa",
      statusRejected: "Ombi limekataliwa",
    },
    pwa: {
      installApp: "Sakinisha Programu ya HP-Helix",
      installDesc: "Pata uchezaji wa haraka sana, upakuaji wa video nje ya mtandao, na matumizi bora ya asili kwenye kifaa chako.",
      installButton: "Sakinisha Programu",
      dismiss: "Baadaye",
      pushTitle: "Taarifa za Kifaa (Push)",
      pushDesc: "Pokea arifa za papo hapo wakati wabunifu unaowafuata wanapopakia video mpya au kuanza matangazo ya moja kwa moja.",
      enablePush: "Washa Taarifa",
      disablePush: "Zima Taarifa",
      sendTestPush: "Tuma Taarifa ya Jaribio",
      testPushSuccess: "Taarifa ya jaribio imetumwa kwenye kivinjari chako!",
      permissionBlocked: "Arifa zimezuiwa. Tafadhali ruhusu kwenye mipangilio ya tovuti ya kivinjari chako.",
      installedBanner: "HP-Helix imesakinishwa na iko tayari kwa matumizi bila mtandao.",
    },
    experiments: {
      testAndCompare: "Jaribu na ulinganishe",
      testAndCompareDesc: "Jaribu picha ndogo hadi 3 na vichwa mbadala na watazamaji wako. Kanuni inaboresha muda wa kutazama au kiwango cha kubofya.",
      startTest: "Anzisha Jaribio la A/B",
      stopTest: "Simamisha Jaribio",
      pickWinner: "Chagua mshindi",
      winningVariant: "Toleo lililoshinda",
      leading: "Inayoongoza",
      slotA: "Nafasi A (Ya Awali)",
      slotB: "Nafasi B (Toleo 2)",
      slotC: "Nafasi C (Toleo 3)",
      addSlotC: "+ Ongeza picha ya 3 (Hiari)",
      removeSlotC: "Ondoa Nafasi C",
      metricLabel: "Kipimo cha Uboreshaji",
      metricWatchTime: "Sehemu ya muda wa kutazama (Inapendekezwa kwa kanuni ya algorithm)",
      metricCtr: "Kiwango cha kubofya (CTR)",
      impressions: "Mionekano",
      clicks: "Mibofyo",
      ctrRate: "CTR",
      watchTimeShare: "Sehemu ya muda wa kutazama",
      runningTest: "Jaribio Linaendelea",
      completedTest: "Jaribio Limekamilika",
      stoppedTest: "Jaribio Limesimamishwa",
      alternateTitle: "Kichwa Mbadala (Hiari)",
      noActiveTest: "Hakuna jaribio linaloendelea kwa video hii.",
      winnerPromotedSuccess: "Toleo limechaguliwa kama picha ndogo na kichwa cha kudumu!",
    },
    memberships: {
      join: "Jiunge",
      memberships: "Uanachama",
      membersOnly: "Wanachama Pekee",
      membershipTiers: "Viwango vya Uanachama",
      joinChannel: "Jiunge na Chaneli",
      joinDescription: "Fungua manufaa maalum, beji za uaminifu, na ufikiaji wa moja kwa moja.",
      monthlyPerks: "Manufaa ya kila mwezi",
      loyaltyBadge: "Beji ya uaminifu",
      chooseTier: "Chagua kiwango chako",
      momoPayment: "Chagua mtandao wa Mobile Money",
      activeMembers: "Wanachama hai",
      monthlyRevenue: "Mapato ya kila mwezi (MRR)",
      createTier: "Ongeza Kiwango cha Uanachama",
      tierName: "Jina la Kiwango",
      priceUsd: "Bei ya mwezi ($ USD)",
      priceCdf: "Bei ya mwezi (CDF)",
      perks: "Manufaa",
      subscribedSince: "Mwanachama tangu",
      monthsMember: "miezi ya uanachama",
    },
    premieres: {
      premiere: "Onyesho la kwanza",
      livePremiere: "Onyesho la moja kwa moja",
      premiereStartsIn: "Onyesho linaanza baada ya",
      remindMe: "Nikumbushe",
      reminderSet: "Kikumbusho kimewekwa",
      premiereCountdown: "Hesabu ya mwisho ya onyesho",
      setAsPremiere: "Panga onyesho la kwanza",
      waitingRoomNotice: "Chumba cha kusubiri kiko wazi. Zungumza na watazamaji wengine kabla ya kuanza.",
      joinLiveChat: "Jiunge na mazungumzo ya moja kwa moja",
    },
    recommendations: {
      upNext: "Inayofuata",
      autoplayOn: "Uchezaji otomatiki umewashwa",
      autoplayOff: "Uchezaji otomatiki umezimwa",
      cancel: "Ghairi",
      playNow: "Cheza Sasa",
      regionalTrending: "Inayovuma DRC na Afrika Mashariki",
      dataSaverActive: "Uokoaji data umewashwa",
      similarCategory: "Kategoria inayofanana",
      fromFavoriteChannel: "Kutoka kwa chaneli unazotazama",
    },
    maintenance: {
      headline: "HP-Helix Video Streaming | Inakuja Hivi Karibuni",
      subtitle: "Jukwaa la video kwa DRC na Afrika Mashariki kwa sasa liko katika awamu ya majaribio ya siri.",
      badge: "Inajengwa na Kuboreshwa",
      devAccess: "Ufikiaji wa Wasanidi Programu / Creator Early Access",
      devModalTitle: "Ufikiaji wa Awali wa Wasanidi na Waumbaji",
      devModalSubtitle: "Ingiza nenosiri la alpha ili kufungua jukwaa zima.",
      passcodePlaceholder: "Ingiza nenosiri la ufikiaji (mfano: helix_alpha_2026)...",
      unlockButton: "Fungua Jukwaa",
      unlocking: "Inafungua...",
      invalidPasscode: "Nenosiri si sahihi. Tafadhali jaribu tena.",
      featureHlsTitle: "Video ya Kasi ya Juu 4K HLS",
      featureHlsDesc: "Kupakia moja kwa moja kwenye Cloudflare Stream CDN bila kupitisha faili kwenye seva.",
      featureMobileTitle: "Uokoaji wa Data ya Simu",
      featureMobileDesc: "Imeboreshwa kwa mitandao ya DRC na Afrika Mashariki pamoja na kuweka akiba nje ya mtandao.",
      featureMonetizationTitle: "Malipo ya Moja kwa Moja kwa Waumbaji",
      featureMonetizationDesc: "Malipo ya papo hapo kupitia M-Pesa, Airtel Money na Orange Money.",
      featureChatTitle: "Maonyesho ya Kwanza na Mazungumzo ya Moja kwa Moja",
      featureChatDesc: "Saa ya kuhesabu inayolingana na mazungumzo ya jamii kwa wakati halisi.",
    },
    clips: {
      clip: "Kipande",
      createClip: "Tengeneza kipande",
      watchFullVideo: "Tazama video kamili",
      shareClip: "Shiriki kipande",
      clipDuration: "Urefu wa kipande",
      titlePlaceholder: "Ongeza jina la kipande chako...",
      titleRequired: "Jina linahitajika (herufi zisizozidi 100)",
      characterCount: "herufi",
      previewLoop: "Muonekano wa kurudia",
      copiedLink: "Kiungo cha kipande kimenakiliwa kwenye ubao wa kunakili!",
      copyLink: "Nakili kiungo",
      shareWhatsApp: "Shiriki kwenye WhatsApp",
      shareX: "Shiriki kwenye X",
      shareFacebook: "Shiriki kwenye Facebook",
      minDurationError: "Kipande lazima kiwe na urefu wa angalau sekunde 5.",
      maxDurationError: "Kipande hakiwezi kuzidi sekunde 60.",
      creating: "Inatengeneza kipande...",
      viewClip: "Tazama kipande",
      viewsCount: "mitazamo",
      startTime: "Mwanzo",
      endTime: "Mwisho",
    },
    studioUpload: {
      uploadVideo: "Pakia video",
      selectFromVideoFrames: "Chagua kutoka fremu za video",
      uploading: "Inapakia...",
      standardVideoBadge: "Video ya Kawaida (16:9)",
      shortBadge: "Video Fupi (9:16)",
      customThumbnail: "Picha maalum",
      uploadCustomThumbnail: "Pakia picha maalum",
      uploadSpeed: "Kasi ya upakiaji",
      timeRemaining: "Muda uliobaki",
      pauseUpload: "Sitisha kwa muda",
      resumeUpload: "Endelea kupakia",
      cancelUpload: "Ghairi",
      visibilityLabel: "Uonekanaji",
      monetizationOptIn: "Washa malipo na uchumi wa Mobile Money",
      mobileMoneyPayouts: "Malipo ya Airtel Money / M-Pesa / Orange Money",
      titleAutoPopulated: "Jina limetolewa moja kwa moja kutoka kwenye faili",
      probingMedia: "Inachambua fomati na kutoa picha ndogo...",
    },
    merch: {
      store: "Duka",
      merchandise: "Bidhaa rasmi",
      officialMerch: "Bidhaa rasmi za muumbaji",
      buyNow: "Nunua sasa",
      order: "Agiza",
      inStock: "Ipo ghalani",
      outOfStock: "Imekwisha",
      deliveryCity: "Mji wa kupeleka",
      deliveryAddress: "Anwani ya makazi",
      buyerName: "Jina kamili",
      buyerPhone: "Nambari ya Mobile Money (M-Pesa / Airtel / Orange)",
      paymentMethod: "Njia ya malipo",
      momoPaymentPrompt: "Ombi la malipo ya Mobile Money litatumwa kwenye simu yako.",
      placeOrder: "Thibitisha agizo kupitia Mobile Money",
      placingOrder: "Inatuma ombi...",
      orderSuccess: "Agizo limethibitishwa!",
      orderSuccessDesc: "Tafadhali weka nambari yako ya siri kwenye simu kukamilisha malipo.",
      pinnedMerch: "Bidhaa zilizoteuliwa",
      allProducts: "Bidhaa zote",
      apparel: "Mavazi",
      musicVinyl: "Muziki na Vinyl",
      accessories: "Vifaa",
      close: "Funga",
    },
    moderation: {
      heldForReview: "Imesitishwa kwa ukaguzi",
      published: "Imechapishwa",
      reportContent: "Ripoti maudhui",
      reportSuccess: "Asante kwa kuripoti. Maudhui haya yanakaguliwa.",
      approve: "Idhinisha",
      remove: "Ondoa",
      blockAuthor: "Mzuie mwandishi",
      approveAll: "Idhinisha yote",
      removeAll: "Ondoa yote",
      commentsQueue: "Msururu wa ukaguzi wa maoni",
      noHeldComments: "Hakuna maoni yaliyositishwa kwa ukaguzi.",
      noPublishedComments: "Bado hakuna maoni yaliyochapishwa.",
      selectAll: "Chagua yote",
      selectedCount: "yamechaguliwa",
      reportReason: "Chagua sababu ya kuripoti",
      reasonSpam: "Upotoshaji au taka (spam)",
      reasonHateSpeech: "Uchochezi au unyanyasaji",
      reasonCopyright: "Ukiukaji wa hakimiliki",
      reasonExplicit: "Maudhui ya ngono au vurugu",
      additionalDetails: "Maelezo ya ziada (hiari)",
      detailsPlaceholder: "Toa maelezo zaidi kusaidia timu yetu...",
      submitReport: "Wasilisha ripoti",
      cancel: "Ghairi",
      authorBlockedNotice: "Mwandishi amezuiwa kwenye kituo hiki.",
      commentApprovedNotice: "Maoni yameidhinishwa na kuchapishwa.",
      commentRemovedNotice: "Maoni yameondolewa.",
    },
    live: {
      live: "MOJA KWA MOJA",
      liveBadge: "MOJA KWA MOJA",
      rewindToLive: "Rudi Mubashara",
      streamEnded: "Matangazo ya moja kwa moja yamemalizika",
      enableDvr: "Wezesha Dirisha la DVR (Rudisha nyuma hadi saa 2)",
      enableDvrDesc: "Huruhusu watazamaji kusitisha na kurudi nyuma katika matangazo ya moja kwa moja.",
      autoPublishVod: "Chapisha Kiotomatiki kama Video (VOD)",
      autoPublishVodDesc: "Hubadilisha matangazo kuwa video ya kudumu mara tu yanapokamilika.",
      liveReplay: "Marudio ya Moja kwa Moja",
      goToLive: "Rudi Mubashara",
      liveStream: "Matangazo ya Moja kwa Moja",
    },
    playlists: {
      playlists: "Orodha za kucheza",
      playlist: "Orodha ya kucheza",
      saveToPlaylist: "Hifadhi kwenye orodha",
      createPlaylist: "Tengeneza orodha mpya",
      playlistTitle: "Jina la orodha",
      privacy: "Faragha",
      public: "Hadharani",
      unlisted: "Isiyoorodheshwa",
      private: "Binafsi",
      shuffle: "Changanya nyimbo",
      loop: "Rudia orodha",
      loopOn: "Rudia orodha : IMEWASHWA",
      loopOff: "Rudia orodha : IMEZIMWA",
      mix: "Mchanganyiko",
      mixDesc: "Mchanganyiko wa kuendelea kulingana na video hii",
      videoCount: "video",
      emptyPlaylist: "Hakuna orodha iliyopatikana. Tengeneza mpya hapa chini !",
    },
    panoramic: {
      panoramicVideo: "Video ya nyuzi 360",
      vrMode: "Hali ya VR",
      cardboardMode: "Hali ya VR Cardboard",
      dragToLook: "Buruta ili kutazama pande zote",
      tiltDevice: "Inamisha simu ili kutazama pande zote",
      resetBearing: "Weka upya Kaskazini",
      compass: "Dira",
      projectionEquirectangular: "Duara Kamili (360° Sphere)",
      projectionFlat: "Mtazamo Bapa",
      recordedIn360: "Video hii ilirekodiwa katika umbizo la 360° / VR",
      recordedIn360Desc: "Wezesha uchezaji wa panoramiki ya 360° na udhibiti wa anga kwa video hii.",
    },
    offlineVault: {
      download: "Pakua",
      downloading: "Inapakua...",
      downloaded: "Imepakuliwa",
      offlineLibrary: "Maktaba ya Nje ya Mtandao",
      storage: "Nafasi ya Uhifadhi",
      storageUsed: "Nafasi Iliyotumika",
      clearAll: "Futa Zote",
      deleteFromDevice: "Futa kwenye kifaa",
      dataSaver: "Kiokoa Data (360p)",
      standardQuality: "Ubora wa Kawaida (480p)",
      highDefinition: "Ubora wa Juu (720p)",
      offlineModeActive: "Hali ya Nje ya Mtandao Iko Hewani",
      offlineModeNotice: "Hali ya nje ya mtandao inatumika - Inacheza kutoka maktaba yako ya ndani",
      noVideosDownloaded: "Hakuna video zilizopakuliwa bado",
      noVideosDescription: "Wakati unatazama video, bonyeza kitufe cha Pakua ili kuihifadhi kwenye kifaa kwa utazamaji bila intaneti wakati wowote.",
      exploreVideos: "Chunguza video za kupakua",
      startDownload: "Anza Kupakua",
      chooseResolution: "Chagua Ubora",
      fullOfflinePlayback: "Uchezaji kamili bila mtandao umewezeshwa kupitia Service Worker na OPFS.",
      alreadySaved: "Tayari imehifadhiwa kwenye maktaba yako ya nje ya mtandao",
      readyForPlayback: "Tayari kwa uchezaji wa haraka bila kutumia data ya simu.",
      removeFromDevice: "Ondoa kwenye Kifaa",
      close: "Funga",
      cancel: "Ghairi",
      confirmClearAll: "Je, una uhakika unataka kufuta video zote zilizopakuliwa kutoka kifaa chako?",
      confirmDelete: "Je, unataka kuondoa video hii iliyopakuliwa kutoka kwenye hifadhi ya ndani?",
      resolution360pDesc: "Ubora wa Chini (360p) - Kiokoa Data (~15 MB)",
      resolution480pDesc: "Ubora wa Wastani (480p) - Kawaida (~35 MB)",
      resolution720pDesc: "Ubora wa Juu (720p) - Ufafanuzi wa Juu (~75 MB)",
    },
    payouts: {
      revenueAndPayouts: "Mapato na Malipo",
      requestPayout: "Omba kutoa pesa",
      availableBalance: "Salio lililopo",
      settlementComplete: "Malipo yamekamilika",
      lifetimeEarnings: "Mapato ya Jumla",
      minimumThresholdNotice: "Kiwango cha chini: $5 USD au CDF 12,500",
      configureWallet: "Sanidi Pochi",
      walletDetails: "Pochi ya Mobile Money na Utoaji",
      phoneNumber: "Nambari ya Simu ya Mobile Money",
      accountName: "Jina la Mwenye Akaunti",
      provider: "Mtoa Huduma wa Malipo",
      monthlyRunRate: "Makadirio ya Mwezi",
      platformFee: "Ada ya Mfumo (20%)",
      netEarnings: "Hisa Halisi ya Mbunifu (80%)",
      pendingPayouts: "Malipo Yanayosubiri",
      withdrawAmount: "Kiasi cha Kutoa",
      history: "Historia ya Malipo",
      statusPaid: "Imelipwa",
      statusProcessing: "Inashughulikiwa",
      statusFailed: "Imeshindikana",
      statusPending: "Inasubiri",
      disbursementQueued: "Ombi la kutoa pesa limetumwa",
      saveWallet: "Hifadhi Mipangilio ya Pochi",
      currency: "Sarafu",
      allRetire: "Toa Zote (100%)",
    },
    coStreaming: {
      coStreaming: "Tangaza Pamoja",
      goLiveTogether: "Tangaza Pamoja",
      inviteGuest: "Alika Mgeni",
      inviteGuestDesc: "Alika mbunifu mwenzako au mtangazaji mwenza kutangaza moja kwa moja pamoja nawe.",
      copyInviteLink: "Nakili Kiungo cha Mwaliko",
      linkCopied: "Kiungo cha mwaliko kimenakiliwa!",
      waitingInGreenroom: "Kwenye chumba cha kusubiri",
      guestConnected: "Mgeni Ameunganishwa",
      sideBySide: "Pande kwa Pande (50/50)",
      pictureInPicture: "Picha Ndani ya Picha",
      enterStream: "Ingia Matangazoni",
      connectingToHost: "Inaunganisha na mtangazaji mkuu...",
      readyToJoin: "Uko tayari kujiunga",
      cameraPreview: "Uhakiki wa Kamera",
      micTest: "Kipimo cha Maikrofoni",
      audioLevel: "Kiwango cha Sauti",
      muteGuestAudio: "Zima Sauti",
      unmuteGuestAudio: "Washa Sauti",
      muteGuestVideo: "Zima Kamera",
      unmuteGuestVideo: "Washa Kamera",
      removeGuest: "Ondoa Mgeni",
      leaveStream: "Ondoka Matangazoni",
      guestDisconnected: "Mgeni ameondoka matangazoni",
      hostBroadcast: "Matangazo ya Mwenyeji",
      liveSplitView: "Muundo wa Watangazaji Wengi",
      guestNameLabel: "Jina Lako la Kuonyesha",
      guestNamePlaceholder: "Ingiza jina lako...",
      onAirBadge: "HEWANI",
      activeGuests: "Watangazaji Wenza Walio Hewani",
      noGuestsJoined: "Bado hakuna wageni waliojiunga. Nakili na ushiriki kiungo kilicho hapo juu.",
    },
    copyright: {
      copyrightMatchTool: "Zana ya Ulinganishaji wa Hakimiliki",
      matchesAndClaims: "Mechi na Madai",
      registeredAssets: "Mali Zilizosajiliwa",
      receivedClaims: "Madai Yaliyopokelewa",
      registerMasterStem: "Sajili Muziki Halisi / Master Stem",
      contentIdWarning: "Onyo la Hakimiliki (Content ID)",
      submitDispute: "Wasilisha Rufaa ya Dai",
      disputeClaim: "Kataa Dai la Hakimiliki",
      requestTakedown: "Omba Kuondolewa",
      monetize: "Pata Mapato",
      track: "Fuatilia Takwimu",
      block: "Zuia",
      disputeReason: "Sababu ya Rufaa",
      disputeExplanation: "Maelezo ya Kina",
      disputeSubmitted: "Rufaa imewasilishwa kwa mafanikio! Ukaguzi unaendelea.",
      disputeFairUse: "Matumizi ya Haki (Fair Use)",
      disputeOriginalCreator: "Muziki wangu halisi 100%",
      disputeLicensed: "Nina leseni halali / Idhini iliyoandikwa",
      audioFingerprintMatch: "Ulinganifu wa Alama ya Sauti",
      confidence: "Kiwango cha Ulinganifu",
      matchedSegment: "Sehemu Iliyolingana",
      releaseClaim: "Ondoa Dai",
      claimReleased: "Dai limeondolewa kwa mafanikio.",
      noMatchesFound: "Hakuna mechi za video zilizogunduliwa kwa orodha yako.",
      noAssetsFound: "Bado hakuna muziki uliosajiliwa. Linda nyimbo zako sasa.",
      noReceivedClaims: "Hakuna madai kwenye video zako. Kila kitu kiko safi.",
      assetTitle: "Jina la Wimbo",
      artist: "Msanii / Mmiliki wa Haki",
      isrc: "Nambari ya ISRC",
      duration: "Muda (sekunde)",
      defaultPolicy: "Sera ya Msingi",
      registerAssetSuccess: "Muziki halisi umesajiliwa na alama ya Content ID kuundwa!",
      filterAll: "Yote",
      filterActive: "Inayotumika",
      filterDisputed: "Inayobishaniwa",
      filterResolved: "Iliyotatuliwa / Iliyoondolewa",
      statusActive: "Inayotumika",
      statusDisputed: "Inayobishaniwa",
      statusReleased: "Iliyoondolewa",
      statusTakedownRequested: "Ombi la Kuondolewa Limetumwa",
    },
  },
};


